import { useOwnershipLabels } from "../../OwnershipLabels";
import { invalidOwnershipPercentageEdges, missingOwnershipPercentageEdges, OWNERSHIP_PERCENTAGE_WARNING, MISSING_OWNERSHIP_PERCENTAGE_WARNING } from "../../../utils/ownershipPercentageValidation";
import { nameFields } from "../../../utils/individualName";
import { ownershipRowNodeIds } from "../../../utils/editOwnershipPercentage";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lottie from "lottie-react";
import countryList from "react-select-country-list";
import { CircleFlag } from "react-circle-flags";
import KycSectionOwnership from "../common/KycSectionOwnership";
import UboIdentificationNotice from "../common/UboIdentificationNotice";
import KycCdbFormsPanel from "../common/KycCdbFormsPanel";
import { OwnershipTreeUaeReadOnly } from "../common/OwnershipTreeUae";
import {
  buildLiveReviewOwnershipSnapshot,
  buildReviewOwnershipApiPayload,
} from "../review/KycReviewOwnershipSection";
import {
  assessUbosFromTree,
  applyUboAssessmentResultToOwnership,
} from "../../../utils/uboAssessment";
import {
  scheduleBodyScrollUnlockCleanup,
  useLockBodyScroll,
} from "../common/common_utils/useLockBodyScroll";
import CountrySelect from "../../../components/ui/CountrySelect";
import IndividualNamePreview from "../../../components/clients/IndividualNamePreview";
import { countryMatchesSearch, toIso3 } from "../../../utils/iso2to3";
import { UPLOAD_FIRST_ENTITY_TYPES } from "../../clientEntityType";
import iconProduction from "../../../images/icon-production.svg";
import iconHolding from "../../../images/icon-holding.svg";
import iconPic from "../../../images/icon-pic.svg";
import iconTrust from "../../../images/icon-trust2.svg";
import iconFoundation from "../../../images/icon-foundation.svg";
import iconQuoted from "../../../images/icon-quoted.svg";
import visualOwnershipBuilderIcon from "../../../images/visual-ownership-builder.svg";
import bubblesChatAssistantIcon from "../../../images/bubbles-chat-assistant.svg";
import iconSettlor from "../../../images/settlor.svg";
import iconHandshake from "../../../images/handshake.svg";
import iconBeneficiary from "../../../images/beneficiary.svg";
import iconIndividual from "../../../images/avatar.svg";
import iconEntity from "../../../images/businessTrade.svg";
import iconPassport from "../../../images/passport.svg";
import iconAddress from "../../../images/earth-location.svg";
import iconPercent from "../../../images/analytics.svg";
import iconCertificate from "../../../images/certificate.svg";
import trustBadgeLottieJson from "../../../images/trustBadge.json";
import "../css/KycClientOwnershipDesktop.css";
import OwnershipOnboarding from "./OwnershipOnboarding";
import OwnershipStructureChoice, { OwnershipStructureChoiceModal } from "./OwnershipStructureChoice";
import { OwnershipChoiceDialog } from "../common/OwnershipChangeDialogs";
import useOwnershipRemoval from "../../hooks/useOwnershipRemoval";
import { allOwnershipParties } from "../../../utils/ownershipPartyRemoval";
import { companyShareholdersNotice } from "../../../utils/companyReadingPrefill";

const COUNTRY_OPTIONS = countryList().getData();

function clean(value) {
  return String(value || "").trim();
}

function cloneForDebug(value) {
  try {
    if (typeof structuredClone === "function") return structuredClone(value);
  } catch (_) {}
  try {
    return JSON.parse(JSON.stringify(value ?? null));
  } catch (_) {
    return value;
  }
}

function summarizeOwnershipRows(rows = []) {
  return (Array.isArray(rows) ? rows : []).map((row, idx) => ({
    idx,
    rowId: clean(row?._rowId),
    treeNodeId: clean(row?.treeNodeId || row?.subjectTreeNodeId),
    relatedShareholderRowId: clean(row?.relatedShareholderRowId),
    relatedShareholderNodeId: clean(row?.relatedShareholderNodeId),
    ...nameFields(row),
    name: clean(row?.name),
    holderType: clean(row?.holderType || row?.shareholderEntityType),
    ownershipPercent: clean(row?.ownershipPercent),
    effectiveOwnershipPercent: clean(row?.effectiveOwnershipPercent),
    basisOfControl: clean(row?.basisOfControl),
  }));
}

function summarizeGraphNodes(nodes = []) {
  return (Array.isArray(nodes) ? nodes : []).map((node, idx) => ({
    idx,
    id: clean(node?.ID || node?.id),
    ...nameFields(node),
    name: clean(node?.name),
    type: clean(node?.type),
    parentIDs: Array.isArray(node?.parentIDs)
      ? node.parentIDs.map((id) => clean(id)).join(",")
      : "",
    percent:
      node?.percent === undefined || node?.percent === null
        ? ""
        : String(node.percent),
  }));
}

function extractMermaidNodeIds(source) {
  const text = Array.isArray(source) ? source.join(";") : String(source || "");
  const ids = new Set();
  const nodeDeclarationRe = /\b([A-Za-z][A-Za-z0-9_]*)\s*(?=\()/g;
  const edgeSourceRe = /\b([A-Za-z][A-Za-z0-9_]*)\s*[-=]+>/g;
  let match;

  while ((match = nodeDeclarationRe.exec(text))) {
    ids.add(match[1]);
  }
  while ((match = edgeSourceRe.exec(text))) {
    ids.add(match[1]);
  }

  return Array.from(ids).filter(
    (id) => !["graph", "classDef", "linkStyle", "style"].includes(id)
  );
}

function tableBackedNodeIds(ownership = {}) {
  const ids = new Set(["X0"]);
  const shareholders = Array.isArray(ownership?.shareholders)
    ? ownership.shareholders
    : [];
  // Same row → node rule as the ownership graph builders.
  ownershipRowNodeIds(shareholders, null, "SH_rev").forEach((id) => ids.add(id));

  const ubos = Array.isArray(ownership?.ubos) ? ownership.ubos : [];
  const uboIds = ownershipRowNodeIds(ubos, null, "UBO_rev");
  ubos.forEach((row, idx) => {
    const id = clean(row?.treeNodeId || row?.subjectTreeNodeId || row?.subjectNodeId);
    if (id || clean(row?.name)) ids.add(uboIds[idx]);
  });

  const arrangements = Array.isArray(ownership?.legalArrangements?.arrangements)
    ? ownership.legalArrangements.arrangements
    : [];
  const arrangementIds = ownershipRowNodeIds(
    arrangements.map((arr) => ({
      treeNodeId: clean(arr?._treeArrangementNodeId || arr?.treeNodeId || arr?.subjectTreeNodeId),
    })),
    null,
    "LA_rev"
  );
  arrangements.forEach((arr, idx) => {
    const id = clean(arr?._treeArrangementNodeId || arr?.treeNodeId || arr?.subjectTreeNodeId);
    if (id || clean(arr?.name)) ids.add(arrangementIds[idx]);
  });

  return Array.from(ids);
}

function buildGraphConsistencyDebug({ ownership, liveOwnershipSnapshot, uboApiPayload }) {
  const storedNodes = Array.isArray(ownership?.assistantTree?.nodes)
    ? ownership.assistantTree.nodes
    : [];
  const liveNodes = Array.isArray(liveOwnershipSnapshot?.nodes)
    ? liveOwnershipSnapshot.nodes
    : [];
  const apiNodes = Array.isArray(uboApiPayload?.nodes) ? uboApiPayload.nodes : [];
  const storedNodeIds = summarizeGraphNodes(storedNodes).map((node) => node.id);
  const liveNodeIds = summarizeGraphNodes(liveNodes).map((node) => node.id);
  const apiNodeIds = summarizeGraphNodes(apiNodes).map((node) => node.id);
  const visibleMermaidIds = extractMermaidNodeIds(liveOwnershipSnapshot?.mermaid);
  const visibleFlowLineIds = extractMermaidNodeIds(liveOwnershipSnapshot?.flowLines || []);
  const tableNodeIds = tableBackedNodeIds(ownership);
  const visibleIds = new Set([...visibleMermaidIds, ...visibleFlowLineIds]);
  const tableIds = new Set(tableNodeIds);

  const isUnsupported = (node) => {
    const id = clean(node?.ID || node?.id);
    if (!id || id === "X0") return false;
    return !visibleIds.has(id) && !tableIds.has(id);
  };

  const staleLiveNodes = liveNodes.filter(isUnsupported);
  const staleApiNodes = apiNodes.filter(isUnsupported);

  return {
    storedNodeIds,
    visibleMermaidIds,
    visibleFlowLineIds,
    tableBackedNodeIds: tableNodeIds,
    liveNodeIds,
    apiNodeIds,
    liveNodesNotVisibleOrTableBacked: summarizeGraphNodes(staleLiveNodes),
    apiNodesNotVisibleOrTableBacked: summarizeGraphNodes(staleApiNodes),
  };
}

function logKycConsistencyDebug(label, consistency) {
  return;
}

function previewOwnershipAfterSetAt(currentOwnership, path, value) {
  if (!Array.isArray(path) || path[0] !== "ownership") return currentOwnership;

  const nextOwnership = cloneForDebug(currentOwnership || {});
  if (path.length === 1) return value;

  let ref = nextOwnership;
  for (let i = 1; i < path.length - 1; i += 1) {
    const key = path[i];
    if (!ref[key] || typeof ref[key] !== "object") ref[key] = {};
    ref = ref[key];
  }
  ref[path[path.length - 1]] = value;
  return nextOwnership;
}

function buildKycOwnershipDebugPayload({
  stage,
  entityName,
  ownership,
  liveOwnershipSnapshot,
  uboApiPayload,
  updatePath,
  updatedValue,
}) {
  const consistency = buildGraphConsistencyDebug({
    ownership,
    liveOwnershipSnapshot,
    uboApiPayload,
  });

  return {
    stage,
    entityName,
    updatePath: Array.isArray(updatePath) ? updatePath : undefined,
    updatedValueSummary: Array.isArray(updatedValue)
      ? summarizeOwnershipRows(updatedValue)
      : undefined,
    storedAssistantTreeMermaid: ownership?.assistantTree?.mermaid || "",
    liveMermaid: liveOwnershipSnapshot?.mermaid || "",
    directShareholders: summarizeOwnershipRows(ownership?.shareholders || []),
    indirectUbos: summarizeOwnershipRows(ownership?.ubos || []),
    legalArrangements: summarizeOwnershipRows(
      ownership?.legalArrangements?.arrangements || []
    ),
    storedAssistantTreeNodes: summarizeGraphNodes(
      ownership?.assistantTree?.nodes || []
    ),
    liveNodes: summarizeGraphNodes(liveOwnershipSnapshot?.nodes || []),
    liveFlowLines: Array.isArray(liveOwnershipSnapshot?.flowLines)
      ? liveOwnershipSnapshot.flowLines
      : [],
    apiPayload: {
      initialAHType: clean(uboApiPayload?.initialAHType),
      nodes: summarizeGraphNodes(uboApiPayload?.nodes || []),
      rawNodes: uboApiPayload?.nodes || [],
    },
    consistency,
  };
}

function logKycCopyDebugPayload(payload) {
  return;
}

function makeRowId(prefix = "row") {
  return `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function emptyShareholderRow() {
  return {
    _rowId: makeRowId("sh"),
    name: "",
    holderType: "",
    beneficialOwnerType: "Beneficial owner",
    nationality: "",
    countryOfResidence: "",
    ownershipPercent: "",
  };
}

const ACCOUNT_HOLDER_OPTIONS = [
  {
    value: "operating",
    title: "Operating company",
    hint:
      "A standard non-quoted company carrying out commercial business activities.",
    icon: "bi-building",
    iconSrc: iconProduction,
  },
  {
    value: "holding",
    title: "Holding company",
    hint:
      "A company primarily used to hold interests in other companies.",
    icon: "bi-diagram-3",
    iconSrc: iconHolding,
  },
  {
    value: "pic",
    title: "Domiciliary company (PIC)",
    hint:
      "A personal investment company used to hold assets or investments.",
    icon: "bi-safe2",
    iconSrc: iconPic,
  },
  {
    value: "trust",
    title: "Trust",
    hint:
      "A legal arrangement where trustees hold or manage assets for beneficiaries.",
    icon: "bi-shield-lock",
    iconSrc: iconTrust,
  },
  {
    value: "foundation",
    title: "Foundation",
    hint:
      "A legal arrangement with separate legal personality for beneficiaries or a purpose.",
    icon: "bi-bank",
    iconSrc: iconFoundation,
  },
  {
    value: "quoted",
    title: "Quoted company",
    hint: "A company listed on a regulated stock exchange.",
    icon: "bi-graph-up-arrow",
    iconSrc: iconQuoted,
  },
];

const METHOD_OPTIONS = [
  {
    value: "guided",
    title: "Guided chat assistant",
    badge: "Best for most",
    hint:
      "Answer a short sequence of questions. We will prepare the ownership organisation chart and summary.",
    icon: "bi-chat-dots",
    iconSrc: bubblesChatAssistantIcon,
  },
  {
    value: "visual",
    title: "Visual ownership builder",
    shortTitle: "Visual builder",
    badgeAlt: "Quick & visual",
    hint:
      "Build the ownership orgnanisation chart by dragging shareholders into the structure.",
    icon: "bi-diagram-3-fill",
    iconSrc: visualOwnershipBuilderIcon,
  },
  {
    value: "manual",
    title: "Manual entry",
    hint:
      "Use direct tables if you already know the shareholders and controlling persons.",
    icon: "bi-pencil-square",
  },
];

const SHOW_MANUAL_ENTRY_METHOD = false;

// Client e-form wording of the ownership removal dialog (documents are never touched there).
const CLIENT_REMOVAL_LABELS = (entityName) => ({
  signatoriesTitle: `These people can also sign for ${entityName}:`,
  kindLabels: { individual: "Person", company: "Company", trust: "Trust", foundation: "Foundation" },
});

const STRUCTURE_FADE_MS = 220;
const STRUCTURE_RESIZE_MS = 300;

// Card content whose height follows its content (structure choice -> completion method).
function MeasuredHeight({ children }) {
  const inner = useRef(null);
  const [height, setHeight] = useState("auto");
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => setHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <motion.div initial={false} animate={{ height }} transition={{ duration: STRUCTURE_RESIZE_MS / 1000, ease: [0.22, 1, 0.36, 1] }}
    style={{ overflow: "hidden" }}>
    <div ref={inner}>{children}</div>
  </motion.div>;
}
const ownershipSectionMotion = {
  initial: { opacity: 0, y: -8, height: 0 },
  animate: { opacity: 1, y: 0, height: "auto" },
  exit: { opacity: 0, y: -8, height: 0 },
  transition: { duration: 0.22, ease: "easeOut" },
};

const ownershipVisualCardMotion = {
  initial: { opacity: 0, y: 8, scale: 0.99 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 6, scale: 0.99 },
  transition: { duration: 0.18, ease: "easeOut" },
};

function InlineDangerMessage({ children, centered = false }) {
  return (
    <div
      className={`kycInlineFieldError ${
        centered ? "kycInlineFieldError--centered" : ""
      }`}
    >
      <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />{" "}
      {children}
    </div>
  );
}

const HOLDING_TYPE_OPTIONS = [
  {
    value: "active",
    label: "Active holding",
    hint:
      "Holds directly or indirectly a majority stake in one or more operating companies. Main purpose is NOT the administration of third-party assets.",
  },
  {
    value: "passive",
    label: "Passive holding",
    hint:
      "Mainly combines and manages assets of a family or a defined group. Often used to enable dividend distributions.",
  },
];

const TRUST_NAME_OPTIONS = [
  {
    value: "trust",
    label: "Client name is the trust name",
    hint:
      "Use this when the client name refers to the trust itself.",
  },
  {
    value: "trustee",
    label: "Client name is the trustee name",
    hint:
      "Use this when the client name refers to the trustee acting for the trust.",
  },
];

function visualBuilderType(profile, holdingKind) {
  if (profile === "operating") return "Non Quoted Operational Company";
  if (profile === "holding") {
    return holdingKind === "passive" ? "HoldingPassive" : "HoldingActive";
  }
  if (profile === "pic") return "PIC";
  if (profile === "trust") return "Trust";
  if (profile === "foundation") return "Foundation";
  if (profile === "quoted") return "Quoted Company";
  return "";
}

function getNodeDisplayName(node, fallback = "") {
  return (
    String(node?.name || "").trim() ||
    String(node?.label || "").trim() ||
    String(fallback || "").trim() ||
    "Unnamed node"
  );
}

function findNodeByApiId(nodes, id) {
  const raw = String(id || "").trim();
  if (!raw) return null;
  const sanitized = raw.replace(/[^a-zA-Z0-9_]/g, "_");
  const candidates = [raw, `G_${sanitized}`, `A_${sanitized}`, `A_${raw}`];
  return (
    nodes.find((node) =>
      candidates.includes(String(node?.ID || node?.id || "").trim())
    ) || null
  );
}

function legalArrangementType(arrangement) {
  return clean(arrangement?.arrangementType) === "Foundation"
    ? "Foundation"
    : "Trust";
}

function legalArrangementRoleIcon(role) {
  const normalizedRole = clean(role).toLowerCase();

  if (normalizedRole === "settlor") return iconSettlor;
  if (normalizedRole === "trustee") return iconHandshake;
  if (normalizedRole === "beneficiary") return iconBeneficiary;
  if (normalizedRole === "protector") return iconSettlor;
  if (normalizedRole === "founder") return iconSettlor;
  if (
    normalizedRole === "council member" ||
    normalizedRole === "council/board"
  ) {
    return iconHandshake;
  }
  if (normalizedRole === "guardian") return iconSettlor;

  return iconIndividual;
}

function legalArrangementParties(arrangement) {
  const parties = Array.isArray(arrangement?.parties)
    ? arrangement.parties
    : Array.isArray(arrangement?.trustParties)
    ? arrangement.trustParties
    : Array.isArray(arrangement?.foundationParties)
    ? arrangement.foundationParties
    : [];

  return parties
    .map((party, idx) => {
      const name = clean(party?.name);
      if (!name) return null;

      const role = clean(party?.role) || "Related individual";
      const kind = clean(party?.trusteeKind || party?.councilKind);
      const meta = [role, kind].filter(Boolean).join(" | ");

      return {
        id: clean(party?._rowId) || `${name}-${idx}`,
        name,
        meta,
        icon: legalArrangementRoleIcon(role),
      };
    })
    .filter(Boolean);
}

function findLegalArrangementForDetectedNode({ ownership, detectedId, node }) {
  const arrangements = Array.isArray(ownership?.legalArrangements?.arrangements)
    ? ownership.legalArrangements.arrangements
    : [];

  if (!arrangements.length) return null;

  const nodeName = getNodeDisplayName(node, detectedId);
  const nodeType = clean(node?.type);
  const idxMatch = clean(detectedId).match(/^LA_rev_(\d+)$/);
  if (idxMatch) {
    const byIndex = arrangements[Number(idxMatch[1])];
    if (byIndex) {
      const sameName = !nodeName || clean(byIndex?.name) === nodeName;
      const sameType = !nodeType || legalArrangementType(byIndex) === nodeType;
      if (sameName && sameType) return byIndex;
    }
  }

  return (
    arrangements.find((arr) => {
      const sameName = clean(arr?.name) === nodeName;
      const sameType = !nodeType || legalArrangementType(arr) === nodeType;
      return sameName && sameType;
    }) || null
  );
}

function profileToAssistantMode(profile, holdingKind) {
  if (profile === "pic") return "pic";
  if (profile === "holding") {
    return holdingKind === "passive" ? "holdingPassive" : "holdingActive";
  }
  return "noneOfAbove";
}

function assistantPercentShouldApply(mode) {
  return mode !== "pic" && mode !== "holdingPassive" && mode !== "quoted";
}

function entityNodeSkipsPercent(category, holdingKind) {
  return category === "pic" || (category === "holding" && holdingKind === "passive");
}

function emptyGuidedNode() {
  return {
    id: makeRowId("ga"),
    type: "",
    entityCategory: "",
    holdingKind: "",
    name: "",
    nationality: "",
    countryOfResidence: "",
    ownershipPercent: "",
    ownersCount: "",
    owners: [],
    trustParties: [],
    trustPartiesSaved: false,
    foundationParties: [],
    foundationPartiesSaved: false,
  };
}

function cloneGuidedNodes(list) {
  return (Array.isArray(list) ? list : []).map((node) => ({
    ...(node || {}),
    owners: cloneGuidedNodes(node?.owners),
    trustParties: (Array.isArray(node?.trustParties) ? node.trustParties : []).map((p) => ({ ...p })),
    foundationParties: (Array.isArray(node?.foundationParties) ? node.foundationParties : []).map((p) => ({ ...p })),
  }));
}

function extractIndividualsFromRoots(nodes) {
  const result = [];
  (Array.isArray(nodes) ? nodes : []).forEach((node) => {
    const type = normalizeGuidedType(node?.type);
    const name = String(node?.name || "").trim();
    if (type === "individual" && name) {
      result.push({ name, nationality: String(node?.nationality || ""), countryOfResidence: String(node?.countryOfResidence || "") });
    }
    if (Array.isArray(node?.owners)) result.push(...extractIndividualsFromRoots(node.owners));
  });
  return result;
}

function getGuidedNodeAtPath(nodes, path) {
  let list = Array.isArray(nodes) ? nodes : [];
  let node = null;
  for (let i = 0; i < path.length; i += 1) {
    node = list[path[i]] || null;
    if (!node) return null;
    list = Array.isArray(node.owners) ? node.owners : [];
  }
  return node;
}

function updateGuidedNodeAtPath(nodes, path, updater) {
  return (Array.isArray(nodes) ? nodes : []).map((node, index) => {
    const current = {
      ...(node || {}),
      owners: cloneGuidedNodes(node?.owners),
      parties: { ...(node?.parties || {}) },
    };
    if (index !== path[0]) return current;
    if (path.length === 1) return updater(current);
    return {
      ...current,
      owners: updateGuidedNodeAtPath(current.owners, path.slice(1), updater),
    };
  });
}

function updateGuidedNodeById(nodes, nodeId, updater) {
  return (Array.isArray(nodes) ? nodes : []).map((node) => {
    const current = { ...(node || {}), owners: cloneGuidedNodes(node?.owners) };
    if (String(current?.id || "") === String(nodeId || "")) return updater(current);
    return { ...current, owners: updateGuidedNodeById(current.owners, nodeId, updater) };
  });
}

function normalizeGuidedType(value) {
  const raw = String(value || "").trim();
  if (raw === "individual") return "individual";
  if (raw === "entity") return "entity";
  if (raw === "trust") return "trust";
  if (raw === "foundation") return "foundation";
  if (raw === "legalArrangement") return "legalArrangement";
  return "";
}

function getGuidedSiblings(nodes, path) {
  if (!Array.isArray(path) || !path.length) return [];
  if (path.length === 1) return Array.isArray(nodes) ? nodes : [];
  const parent = getGuidedNodeAtPath(nodes, path.slice(0, -1));
  return Array.isArray(parent?.owners) ? parent.owners : [];
}

function ordinal(n) {
  const v = n % 100;
  const suffix = ["th", "st", "nd", "rd"];
  return n + (suffix[(v - 20) % 10] || suffix[v] || suffix[0]);
}

function makeTrustParty(role) {
  return { _rowId: makeRowId("tp"), role, name: "", trusteeKind: "", nationality: "", domicile: "" };
}
function makeDefaultTrustParties() {
  return [makeTrustParty("Settlor"), makeTrustParty("Trustee"), makeTrustParty("Beneficiary")];
}
function makeFoundationParty(role) {
  return { _rowId: makeRowId("fp"), role, name: "", councilKind: "", nationality: "", domicile: "" };
}
function makeDefaultFoundationParties() {
  return [makeFoundationParty("Founder"), makeFoundationParty("Council member"), makeFoundationParty("Beneficiary")];
}

function isTrustPartyComplete(party) {
  const name = String(party?.name || "").trim();
  const nationality = String(party?.nationality || "").trim();
  const domicile = String(party?.domicile || "").trim();
  if (!name) return false;
  if (String(party?.role || "") === "Trustee") {
    const kind = String(party?.trusteeKind || "").trim().toLowerCase();
    if (!["individual", "entity"].includes(kind)) return false;
    if (kind === "entity") return !!domicile;
    return !!nationality && !!domicile;
  }
  return !!nationality && !!domicile;
}
function isFoundationPartyComplete(party) {
  const name = String(party?.name || "").trim();
  const nationality = String(party?.nationality || "").trim();
  const domicile = String(party?.domicile || "").trim();
  if (!name) return false;
  if (String(party?.role || "") === "Council member") {
    const kind = String(party?.councilKind || "").trim().toLowerCase();
    if (!["individual", "entity"].includes(kind)) return false;
    if (kind === "entity") return !!domicile;
    return !!nationality && !!domicile;
  }
  return !!nationality && !!domicile;
}


function toPercentNumber(value) {
  const n = Number(String(value || "").trim());
  return Number.isFinite(n) ? n : 0;
}

export function ownershipPercentWarning(snapshot) {
  const nodes = Array.isArray(snapshot?.nodes) ? snapshot.nodes : [];
  if (!nodes.length) return "";

  const hasMissingName = (node) => {
    const name = String(node?.name || "").trim();
    return !name || name === "…" || name.toLowerCase() === "(unnamed)";
  };

  const childCountByParent = new Map();
  nodes.forEach((node) => {
    const parents = Array.isArray(node?.parentIDs) ? node.parentIDs : [];
    const parentId = String(parents[0] || "").trim();
    if (!parentId) return;

    childCountByParent.set(parentId, (childCountByParent.get(parentId) || 0) + 1);

  });

  const parentNeedsChildren = (node) => {
    const nodeId = String(node?.ID || node?.id || "").trim();
    if (!nodeId) return false;
    const type = String(
      nodeId === "X0" ? snapshot?.initialAHType || node?.type || "" : node?.type || ""
    ).trim();
    const category = String(node?.entityCategory || "").trim();
    if (!type || type === "Individual" || type === "Trust" || type === "Foundation") return false;
    if (category === "trust" || category === "foundation") return false;
    return (
      type === "PIC" ||
      type === "HoldingActive" ||
      type === "HoldingPassive" ||
      type === "Non Quoted Operational Company" ||
      type === "Quoted Company" ||
      category === "pic" ||
      category === "holding" ||
      category === "nonQuotedOperational" ||
      category === "quoted"
    );
  };

  const entityWithMissingName = nodes.find((node) => {
    if (!parentNeedsChildren(node)) return false;
    return hasMissingName(node);
  });
  if (entityWithMissingName) {
    return "The name of an entity is missing";
  }

  if (nodes.some(node =>
    node?.type === "Individual" &&
    node?.parentIDs?.length &&
    hasMissingName(node)
  )) {
    return "The name of a shareholder is missing";
  }

  const parentsWithoutChildren = [];
  for (const node of nodes) {
    const nodeId = String(node?.ID || node?.id || "").trim();
    if (parentNeedsChildren(node) && !childCountByParent.get(nodeId)) {
      parentsWithoutChildren.push(node);
    }
  }
  if (parentsWithoutChildren.length === 1) {
    const parentName = String(parentsWithoutChildren[0]?.name || "").trim();
    return `No shareholder are indicated for ${parentName || "an entity"}`;
  }
  if (parentsWithoutChildren.length > 1) {
    return "No shareholder are indicated for some entities";
  }

  if (missingOwnershipPercentageEdges(snapshot).length) {
    return MISSING_OWNERSHIP_PERCENTAGE_WARNING;
  }

  if (invalidOwnershipPercentageEdges(snapshot).length) {
    return OWNERSHIP_PERCENTAGE_WARNING;
  }

  return "";
}

function formatPercentInputValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

function clampPercentInputValue(value, max = 100) {
  const raw = String(value ?? "")
    .replace(",", ".")
    .replace(/[^\d.]/g, "")
    .replace(/(\..*)\./g, "$1")
    .replace(/^(\d+)(\.\d{0,2})?.*$/, "$1$2")
    .trim();
  if (!raw) return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  const safeMax = Math.max(0, Math.min(100, Number(max) || 0));
  if (n < 0) return "0";
  if (n > safeMax) return formatPercentInputValue(safeMax);
  return raw;
}

function getGuidedPercentForNode(nodes, path, node) {
  const siblings = getGuidedSiblings(nodes, path);
  if (siblings.length <= 1) return "100";
  const idx = path[path.length - 1];
  const isLast = idx === siblings.length - 1;
  if (!isLast) return String(node?.ownershipPercent || "");
  const used = siblings.reduce((sum, sibling, siblingIdx) => {
    if (siblingIdx === idx) return sum;
    return sum + toPercentNumber(sibling?.ownershipPercent);
  }, 0);
  const remaining = Math.max(0, Math.round((100 - used) * 100) / 100);
  return String(node?.ownershipPercent || remaining || "");
}

function guidedNodeComplete(node, nodes, path, parentUsesPercent) {
  const type = normalizeGuidedType(node?.type);
  if (!type) return false;
  if (type === "legalArrangement") return false;
  if (type === "entity" && !String(node?.entityCategory || "").trim()) return false;
  if (
    type === "entity" &&
    node?.entityCategory === "holding" &&
    !String(node?.holdingKind || "").trim()
  ) {
    return false;
  }
  if (!String(node?.name || "").trim()) return false;
  if (
    type === "individual" &&
    (!String(node?.nationality || "").trim() ||
      !String(node?.countryOfResidence || "").trim())
  ) {
    return false;
  }
  if (
    type === "entity" &&
    !String(node?.countryOfResidence || "").trim()
  ) {
    return false;
  }
  if (parentUsesPercent && !getGuidedPercentForNode(nodes, path, node)) return false;
  if (type === "trust") {
    const parties = Array.isArray(node?.trustParties) ? node.trustParties : [];
    return !!node?.trustPartiesSaved && parties.length > 0 && parties.every(isTrustPartyComplete);
  }
  if (type === "foundation") {
    const parties = Array.isArray(node?.foundationParties) ? node.foundationParties : [];
    return !!node?.foundationPartiesSaved && parties.length > 0 && parties.every(isFoundationPartyComplete);
  }
  if (type === "entity" && node?.entityCategory !== "quoted") {
    const count = Number.parseInt(String(node?.ownersCount || ""), 10);
    if (!Number.isFinite(count) || count < 1) return false;
    return (Array.isArray(node?.owners) ? node.owners.length : 0) >= count;
  }
  return true;
}

function findNextGuidedPath(nodes, assistantMode) {
  function walk(list, pathPrefix, parentUsesPercent) {
    for (let i = 0; i < list.length; i += 1) {
      const node = list[i] || {};
      const path = [...pathPrefix, i];
      if (!guidedNodeComplete(node, nodes, path, parentUsesPercent)) return path;
      const type = normalizeGuidedType(node?.type);
      if (type === "entity" && node?.entityCategory !== "quoted") {
        const childUsesPercent = !entityNodeSkipsPercent(
          String(node?.entityCategory || ""),
          String(node?.holdingKind || "")
        );
        const childPath = walk(node?.owners || [], path, childUsesPercent);
        if (childPath) return childPath;
      }
    }
    return null;
  }
  return walk(nodes || [], [], assistantPercentShouldApply(assistantMode));
}

function collectGuidedLeafRows(nodes, assistantMode, rootRowId = "", parentFactor = 100, parentUsesPercent = null, depth = 0) {
  const out = [];
  const usesPercent =
    parentUsesPercent === null
      ? assistantPercentShouldApply(assistantMode)
      : parentUsesPercent;

  (Array.isArray(nodes) ? nodes : []).forEach((node, idx) => {
    const path = [idx];
    const pct = usesPercent ? toPercentNumber(getGuidedPercentForNode(nodes, path, node)) : 100;
    const factor = usesPercent ? (parentFactor * pct) / 100 : parentFactor;
    const type = normalizeGuidedType(node?.type);
    const currentRoot = rootRowId || String(node?._generatedRowId || "");

    if (type === "entity" && node?.entityCategory !== "quoted" && Array.isArray(node?.owners) && node.owners.length) {
      const childUsesPercent = !entityNodeSkipsPercent(
        String(node?.entityCategory || ""),
        String(node?.holdingKind || "")
      );
      out.push(
        ...collectGuidedLeafRows(
          node.owners,
          assistantMode,
          currentRoot,
          factor,
          childUsesPercent,
          depth + 1
        )
      );
    } else {
      // The row keeps only the % held in the direct parent, and none where that parent (or the
      // client) does not carry a % — never a cumulated % or a default 100%.
      out.push({ node, rootRowId: currentRoot, depth, effectiveOwnershipPercent: usesPercent ? String(Math.round(pct * 100) / 100) : "" });
    }
  });
  return out;
}

function escapeMermaidLabel(value) {
  return String(value || "").replace(/"/g, "&quot;").replace(/\n/g, "<br/>");
}

function escapeAssistantTreeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function toAssistantTreeHtmlLabel(html) {
  return String(html || "")
    .replace(/\r?\n/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\\/g, "\\\\")
    .replace(/"/g, "'");
}

function toDisplayTitleCase(value) {
  return String(value || "").replace(/\p{L}[\p{L}'-]*/gu, (word) => {
    const [first = "", ...rest] = Array.from(word);
    return `${first.toLocaleUpperCase()}${rest.join("")}`;
  });
}

function formatGuidedPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return `${Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100)}%`;
}

function formatCountryFull(value) {
  const code = String(value || "").trim();
  if (!code) return "";
  return COUNTRY_OPTIONS.find((opt) => opt.value === code)?.label || code;
}

function toTitleCase(value) {
  return String(value || "").replace(/(^|\s)\S/g, (letter) =>
    letter.toUpperCase()
  );
}

function formatGuidedNodeType(node) {
  const type = normalizeGuidedType(node?.type);
  const named = !!String(node?.name || "").trim();
  if (type === "individual") return "Individual";
  if (type === "trust") return "Trust";
  if (type === "foundation") return "Foundation";
  if (type === "entity") {
    // A company already named (company documents) shows in the tree before its type is answered: "?".
    if (!node?.entityCategory) return named ? "?" : "";
    if (node?.entityCategory === "pic") return "PIC";
    if (node?.entityCategory === "quoted") return "Quoted Company";
    if (node?.entityCategory === "holding") {
      if (!node?.holdingKind) return named ? "Holding" : "";
      return node?.holdingKind === "passive"
        ? "Holding (Passive)"
        : "Holding (Active)";
    }
    return "OpCo";
  }
  return "Entity";
}

function assistantTreeIconClassForType(typeText) {
  if (typeText === "Individual") return "fa fa-user";
  if (typeText === "Trust" || typeText === "Foundation") return "fa fa-bookmark";
  return "fa fa-building";
}

// Diagram/tree node ID of a guided-assistant node: the same ID as its saved row's treeNodeId,
// so the review graph can patch the saved tree from table edits.
export function guidedNodeId(node) {
  return String(node?.id || "").replace(/[^a-zA-Z0-9_]/g, "_");
}

// Node type as used by the ownership tree and the UBO APIs.
export function guidedApiType(node) {
  const type = normalizeGuidedType(node?.type);
  if (type === "individual") return "Individual";
  if (type === "trust") return "Trust";
  if (type === "foundation") return "Foundation";
  if (node?.entityCategory === "pic") return "PIC";
  if (node?.entityCategory === "quoted") return "Quoted Company";
  if (node?.entityCategory === "holding") return node?.holdingKind === "passive" ? "HoldingPassive" : "HoldingActive";
  return "Non Quoted Operational Company";
}

function buildGuidedTreeNodeHtml(name, typeText, isRoot = false) {
  const safeType = escapeAssistantTreeHtml(typeText || "Entity");
  const safeName = escapeAssistantTreeHtml(
    toDisplayTitleCase(name || (isRoot ? "" : "(unnamed)"))
  );
  const iconCls = assistantTreeIconClassForType(typeText);
  return `
    <div style='padding:0px 10px; line-height:1.05; text-align:center;'>
      <div style='display:flex;align-items:center;justify-content:center;gap:6px;white-space:nowrap;'>
        <i class='${iconCls}' style='font-size:15px;line-height:1;'></i>
        <span style='font-weight:600;font-size:15px;line-height:1;'>${safeType}</span>
      </div>
      <div style='margin-top:8px;font-size:12px;opacity:.85;white-space:nowrap;line-height:1;'>${safeName}</div>
    </div>
  `;
}

export function buildGuidedAssistantSnapshot(entityName, roots, assistantMode) {
  const flowLines = [];
  const accountHolderType =
    assistantMode === "pic"
      ? "Domiciliary Company (PIC)"
      : assistantMode === "holdingActive"
      ? "Holding (Active)"
      : assistantMode === "holdingPassive"
      ? "Holding (Passive)"
      : "Operating Company";
  const graphLines = [
    "graph TD",
    `X0("${toAssistantTreeHtmlLabel(
      buildGuidedTreeNodeHtml(entityName || "Account holder", accountHolderType, true)
    )}")`,
  ];

  // The full chain is also kept as tree nodes (same shape as the visual builder's), so the
  // UBO assessment, CDB forms and review graph use exactly the structure drawn here.
  const treeNodes = [{
    ID: "X0",
    level: 0,
    parentIDs: [],
    type: accountHolderType,
    name: String(entityName || "").trim(),
    percent: 100,
  }];

  function walk(nodes, parentId = "X0", parentShowPercent = assistantPercentShouldApply(assistantMode), parentPath = [], parentChain = []) {
    (Array.isArray(nodes) ? nodes : []).forEach((node, nodeIdx) => {
      const nodeType = normalizeGuidedType(node?.type);
      if (!nodeType || nodeType === "legalArrangement") return;
      const nodePath = [...parentPath, nodeIdx];
      const nodeId = guidedNodeId(node) || makeRowId("ga");
      const typeLabel = formatGuidedNodeType(node);
      if (!typeLabel) return;
      const html = buildGuidedTreeNodeHtml(String(node?.name || "").trim(), typeLabel, false);
      const label = toAssistantTreeHtmlLabel(html);
      graphLines.push(`${nodeId}("${label}")`);
      const pct = parentShowPercent
        ? String(getGuidedPercentForNode(roots, nodePath, node) || "").trim()
        : "";
      if (parentShowPercent && pct) {
        const pctLabel = formatGuidedPercent(pct);
        graphLines.push(`${parentId} -->|"${escapeMermaidLabel(pctLabel)}"| ${nodeId}`);
        flowLines.push(`${parentId} -->|"${pctLabel}"| ${nodeId}("${label}")`);
      } else {
        graphLines.push(`${parentId} --> ${nodeId}`);
        flowLines.push(`${parentId} --> ${nodeId}("${label}")`);
      }
      const isIndividual = nodeType === "individual";
      const pctNumber = Number(String(pct).replace(",", "."));
      treeNodes.push({
        ID: nodeId,
        level: nodePath.length,
        parentIDs: [parentId, ...parentChain],
        type: guidedApiType(node),
        ...(isIndividual ? nameFields(node) : {}),
        name: String(node?.name || "").trim(),
        // % held in the parent company; below a PIC / passive holding / trust the % is not meaningful.
        percent: parentShowPercent && Number.isFinite(pctNumber) && pct !== "" ? pctNumber : undefined,
        nationality: isIndividual ? String(node?.nationality || "") : "",
        countryOfResidence: String(node?.countryOfResidence || node?.countryOfIncorporation || ""),
        roles: null,
      });
      const childShowPercent = !entityNodeSkipsPercent(
        String(node?.entityCategory || ""),
        String(node?.holdingKind || "")
      );
      walk(node?.owners || [], nodeId, childShowPercent, nodePath, [parentId, ...parentChain]);
    });
  }

  walk(roots);
  graphLines.push(
    "classDef default fill:#BAD9CB,stroke:#BAD9CB,stroke-width:0px,color:#484848"
  );
  graphLines.push("linkStyle default stroke-width:2px,fill:none,stroke:#B0B0B0");

  return {
    mermaid: graphLines.join("\n"),
    initialAHType: accountHolderType,
    source: "desktopGuidedAssistant",
    labelMode: "default",
    nodes: treeNodes,
    flowLines,
  };
}

function formatGuidedNodeSummary(node, roots, path, assistantMode) {
  const type = normalizeGuidedType(node?.type);
  const name = String(node?.name || "").trim();
  if (!name || !type || type === "legalArrangement") return "";
  const typeLabel = formatGuidedNodeType(node);
  const usesPercent =
    path.length <= 1
      ? assistantPercentShouldApply(assistantMode)
      : !entityNodeSkipsPercent(
          String(getGuidedNodeAtPath(roots, path.slice(0, -1))?.entityCategory || ""),
          String(getGuidedNodeAtPath(roots, path.slice(0, -1))?.holdingKind || "")
        );
  const pct = usesPercent ? getGuidedPercentForNode(roots, path, node) : "";
  return `${name} (${typeLabel})${pct ? ` - ${formatGuidedPercent(pct)}` : ""}`;
}

function quotedCompanyShareholdersNotRequiredText(name) {
  const entityLabel = String(name || "this entity").trim();
  return `Shareholders of ${entityLabel} are not required because ${entityLabel} is a quoted company`;
}

function SelectionCard({
  option,
  active,
  onClick,
  className = "",
  disabled = false,
  forceThreeLineModel = false,
  onHintLineCountChange,
  children,
}) {
  const titleRef = useRef(null);
  const hintRef = useRef(null);
  const [titleFontSize, setTitleFontSize] = useState(15);
  const [useShortTitle, setUseShortTitle] = useState(false);
  const [hintLineCount, setHintLineCount] = useState(1);
  const renderedTitle = useShortTitle && option.shortTitle ? option.shortTitle : option.title;

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return undefined;

    let frame = 0;
    const measure = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const style = window.getComputedStyle(el);
        const computedFontSize = parseFloat(style.fontSize) || 15;
        const gap = parseFloat(style.columnGap || style.gap) || 8;
        const badges = Array.from(el.querySelectorAll(".kycClientOwnChoiceBadge"));
        const badgesWidth = badges.reduce((sum, badge) => sum + badge.scrollWidth, 0);
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (context && option.shortTitle) {
          context.font = `${style.fontWeight || 600} 15px ${style.fontFamily || "Roboto"}`;
          const fullTitleWidth = context.measureText(option.title).width;
          const fullRowWidth = fullTitleWidth + badgesWidth + (badges.length ? gap : 0);
          setUseShortTitle(fullRowWidth > el.clientWidth + 1);
        }
        if (!el.clientWidth || el.scrollWidth <= el.clientWidth + 1) {
          setTitleFontSize(15);
          return;
        }
        const nextFontSize = Math.max(
          12,
          Math.min(15, Math.floor((computedFontSize * el.clientWidth * 10) / el.scrollWidth) / 10)
        );
        setTitleFontSize(nextFontSize);
      });
    };

    measure();

    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    resizeObserver?.observe(el);
    window.addEventListener("resize", measure);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [option.title, option.shortTitle, option.badge, option.badgeAlt, useShortTitle]);

  useEffect(() => {
    const el = hintRef.current;
    if (!el) return undefined;

    let frame = 0;
    const measure = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight) || 1;
        const nextLineCount = Math.max(1, Math.round(el.scrollHeight / lineHeight));
        setHintLineCount((prev) => (prev === nextLineCount ? prev : nextLineCount));
        onHintLineCountChange?.(nextLineCount);
      });
    };

    measure();

    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    resizeObserver?.observe(el);
    window.addEventListener("resize", measure);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [option.hint]);

  return (
    <button
      type="button"
      className={`kycClientOwnChoice ${active ? "is-active" : ""} ${
        disabled ? "is-disabled" : ""
      } ${
        forceThreeLineModel || hintLineCount >= 3 ? "is-three-line-hint" : ""
      } ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="kycClientOwnChoiceIcon">
        {option.iconSrc ? (
          <img src={option.iconSrc} alt="" aria-hidden="true" />
        ) : (
          <i className={`bi ${option.icon}`} aria-hidden="true" />
        )}
      </span>
      <span className="kycClientOwnChoiceCopy">
        <span
          ref={titleRef}
          className="kycClientOwnChoiceTitle"
          style={{ "--choice-title-font-size": `${titleFontSize}px` }}
        >
          {renderedTitle}
          {option.badge ? (
            <span className="kycClientOwnChoiceBadge">{option.badge}</span>
          ) : null}
          {option.badgeAlt ? (
            <span className="kycClientOwnChoiceBadge kycClientOwnChoiceBadge--blue">{option.badgeAlt}</span>
          ) : null}
        </span>
        <span ref={hintRef} className="kycClientOwnChoiceHint">{option.hint}</span>
        {children}
      </span>
    </button>
  );
}

function VisualBuilderTutorial({ onStart, onClose, builderAlreadyOpen = false }) {
  return (
    <div className="kycClientOwnTutorialOverlay" onClick={onClose}>
      <div className="kycClientOwnTutorialModal" onClick={e => e.stopPropagation()}>
        <div className="kycClientOwnAssistantHeader">
          <div>
            <div className="kycClientOwnEyebrow">How it works</div>
            <h6>Follow these steps to build your ownership structure</h6>
          </div>
        </div>

        <div className="kycClientOwnTutorialModalBody">
          <OwnershipOnboarding />
        </div>

        <div className="kycClientOwnTutorialModalFooter">
          <button type="button" className="kycClientOwnTextBtn" onClick={onClose}>
            {builderAlreadyOpen ? "Back to Builder" : "Close"}
          </button>
          {!builderAlreadyOpen ? (
            <button
              type="button"
              className="kycClientOwnPrimaryBtn kycClientOwnTutorialStartBtn"
              onClick={onStart}
            >
              Start visual builder
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DesktopGuidedOwnershipAssistant({
  profile,
  separateNames = false,
  holdingKind,
  entityName,
  onGenerateOwnership,
  onExitIncomplete,
  onRootsChange,
  summaryContent = null,
  initialSubmitted = false,
  // Direct shareholders read in the company documents (a company among them; see companyReadingPrefill): the
  // chat starts with them filled in, at the first question left (normally the type of the company).
  documentShareholders = null,
}) {
  const { ownershipLabel } = useOwnershipLabels();
  const assistantMode = profileToAssistantMode(profile, holdingKind);
  const [open, setOpen] = useState(!initialSubmitted);
  // Cleared by "Restart": the client then answers from scratch.
  const [documentStart, setDocumentStart] = useState(() => {
    const list = (Array.isArray(documentShareholders) ? documentShareholders : []).filter((p) => String(p?.name || "").trim());
    if (initialSubmitted || !list.some((p) => p.kind === "company")) return null;
    return {
      companies: list.filter((p) => p.kind === "company").map((p) => String(p.name).trim()),
      roots: list.map((p) => ({
        ...emptyGuidedNode(),
        type: p.kind === "company" ? "entity" : "individual",
        name: String(p.name).trim(),
        nationality: p.kind === "company" ? "" : String(p.nationality || ""),
        // A company's country of incorporation is kept as countryOfResidence (as when typed in the chat).
        countryOfResidence: String((p.kind === "company" ? p.country : p.countryOfResidence) || ""),
        ownershipPercent: Number.isFinite(p.percent) ? String(p.percent) : "",
      })),
    };
  });
  const [directCount, setDirectCount] = useState(documentStart ? String(documentStart.roots.length) : "");
  const [directCountRaw, setDirectCountRaw] = useState("");
  const [showDirectCountInput, setShowDirectCountInput] = useState(false);
  const [ownersCountRaw, setOwnersCountRaw] = useState("");
  const [showOwnersCountInput, setShowOwnersCountInput] = useState(false);
  const [nameRaw, setNameRaw] = useState("");
  const [partiesModalOpen, setPartiesModalOpen] = useState(false);
  const [countryPicker, setCountryPicker] = useState(null);
  const [countryPickerClosing, setCountryPickerClosing] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [roots, setRoots] = useState(documentStart ? documentStart.roots : []);
  const [submitted, setSubmitted] = useState(!!initialSubmitted);
  const [percentRaw, setPercentRaw] = useState("");
  const chatPaneRef = useRef(null);
  const countrySearchRef = useRef(null);
  const countryPickerCloseTimerRef = useRef(null);
  useLockBodyScroll(open);

  useEffect(() => {
    if (initialSubmitted) {
      setSubmitted(true);
      setOpen(false);
    }
  }, [initialSubmitted]);

  const currentPath = directCount ? findNextGuidedPath(roots, assistantMode) : null;
  const currentNode = currentPath ? getGuidedNodeAtPath(roots, currentPath) : null;
  const currentType = normalizeGuidedType(currentNode?.type);
  const currentPathKey = currentPath ? currentPath.join(".") : "";
  const liveAssistantSnapshot = useMemo(
    () => buildGuidedAssistantSnapshot(entityName, roots, assistantMode),
    [assistantMode, entityName, roots]
  );

  const liveHighlightNodeId = useMemo(() => {
    function mermaidId(node) {
      return node?.id ? guidedNodeId(node) : "";
    }
    function parentId(path) {
      if (!path || path.length <= 1) return "X0";
      const p = getGuidedNodeAtPath(roots, path.slice(0, -1));
      return mermaidId(p) || "X0";
    }
    if (!currentPath) return directCount ? "" : "X0";
    const node = getGuidedNodeAtPath(roots, currentPath);
    const nodeType = normalizeGuidedType(node?.type);
    const isSelectionStep =
      !nodeType ||
      nodeType === "legalArrangement" ||
      (nodeType === "entity" && !node?.entityCategory) ||
      (nodeType === "entity" && node?.entityCategory === "holding" && !node?.holdingKind);
    return isSelectionStep ? parentId(currentPath) : (mermaidId(node) || parentId(currentPath));
  }, [currentPathKey, directCount, roots]); // eslint-disable-line
  const filteredCountryOptions = useMemo(
    () => COUNTRY_OPTIONS.filter((opt) => countryMatchesSearch(opt, countrySearch)),
    [countrySearch]
  );

  useEffect(() => {
    setOwnersCountRaw("");
    setShowOwnersCountInput(false);
    setCountryPicker(null);
    setCountryPickerClosing(false);
    setCountrySearch("");
    setNameRaw(currentNode?.name || "");
    setPercentRaw(currentNode?.ownershipPercent || "");
  }, [currentPathKey]); // eslint-disable-line

  useEffect(() => {
    return () => {
      if (countryPickerCloseTimerRef.current) {
        window.clearTimeout(countryPickerCloseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!countryPicker) return;
    const frame = window.requestAnimationFrame(() => {
      countrySearchRef.current?.focus();
      countrySearchRef.current?.select?.();
    });
    const timer = window.setTimeout(() => {
      countrySearchRef.current?.focus();
      countrySearchRef.current?.select?.();
    }, 120);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [countryPicker]);

  useEffect(() => {
    if (!open) return;
    const pane = chatPaneRef.current;
    if (!pane) return;

    requestAnimationFrame(() => {
      pane.scrollTo({
        top: pane.scrollHeight,
        behavior: "smooth",
      });
    });
  }, [currentPath, currentType, directCount, open, roots]);

  function setRootCount(value) {
    const count = Math.max(1, Math.min(99, Number.parseInt(String(value || ""), 10) || 0));
    setDirectCount(String(count));
    setRoots((prev) => {
      const next = cloneGuidedNodes(prev).slice(0, count);
      while (next.length < count) next.push(emptyGuidedNode());
      return next;
    });
  }

  function updateCurrentNode(patch) {
    if (!currentPath) return;
    setRoots((prev) =>
      updateGuidedNodeAtPath(prev, currentPath, (node) => ({
        ...node,
        ...(typeof patch === "function" ? patch(node) : patch),
      }))
    );
  }

  function setCurrentType(type) {
    updateCurrentNode({
      type,
      entityCategory: "",
      holdingKind: "",
      ownersCount: "",
      owners: [],
      trustParties: [],
      trustPartiesSaved: false,
      foundationParties: [],
      foundationPartiesSaved: false,
    });
    setPartiesModalOpen(false);
  }

  function updateCurrentNodeParty(collection, partyId, patch) {
    if (!currentPath) return;
    const savedKey =
      collection === "foundationParties"
        ? "foundationPartiesSaved"
        : "trustPartiesSaved";
    setRoots((prev) =>
      updateGuidedNodeAtPath(prev, currentPath, (node) => {
        const parties = Array.isArray(node?.[collection]) ? node[collection] : [];
        return {
          ...node,
          [savedKey]: false,
          [collection]: parties.map((p) =>
            String(p?._rowId || "") === String(partyId) ? { ...p, ...patch } : p
          ),
        };
      })
    );
  }

  function addCurrentNodeParty(collection, role) {
    if (!currentPath) return;
    const savedKey =
      collection === "foundationParties"
        ? "foundationPartiesSaved"
        : "trustPartiesSaved";
    setRoots((prev) =>
      updateGuidedNodeAtPath(prev, currentPath, (node) => {
        const parties = Array.isArray(node?.[collection]) ? node[collection] : [];
        const newParty = collection === "foundationParties" ? makeFoundationParty(role) : makeTrustParty(role);
        return { ...node, [savedKey]: false, [collection]: [...parties, newParty] };
      })
    );
  }

  function removeCurrentNodeParty(collection, partyId) {
    if (!currentPath) return;
    const savedKey =
      collection === "foundationParties"
        ? "foundationPartiesSaved"
        : "trustPartiesSaved";
    setRoots((prev) =>
      updateGuidedNodeAtPath(prev, currentPath, (node) => {
        const parties = Array.isArray(node?.[collection]) ? node[collection] : [];
        return {
          ...node,
          [savedKey]: false,
          [collection]: parties.filter((p) => String(p?._rowId || "") !== String(partyId)),
        };
      })
    );
  }

  function saveCurrentNodeParties(collection) {
    if (!currentPath) return;
    const savedKey =
      collection === "foundationParties"
        ? "foundationPartiesSaved"
        : "trustPartiesSaved";
    updateCurrentNode({ [savedKey]: true });
    setPartiesModalOpen(false);
  }

  function setCurrentOwnersCount(value) {
    const count = Math.max(1, Math.min(99, Number.parseInt(String(value || ""), 10) || 0));
    updateCurrentNode((node) => {
      const owners = cloneGuidedNodes(node?.owners || []).slice(0, count);
      while (owners.length < count) owners.push(emptyGuidedNode());
      return { ownersCount: String(count), owners };
    });
  }

  function openCurrentNodeCountryPicker(field, title) {
    if (countryPickerCloseTimerRef.current) {
      window.clearTimeout(countryPickerCloseTimerRef.current);
      countryPickerCloseTimerRef.current = null;
    }
    setCountrySearch("");
    setCountryPickerClosing(false);
    setCountryPicker({
      field,
      title,
      value: String(currentNode?.[field] || ""),
    });
  }

  function closeCountryPicker() {
    if (!countryPicker) return;
    setCountryPickerClosing(true);
    if (countryPickerCloseTimerRef.current) {
      window.clearTimeout(countryPickerCloseTimerRef.current);
    }
    countryPickerCloseTimerRef.current = window.setTimeout(() => {
      setCountryPicker(null);
      setCountryPickerClosing(false);
      setCountrySearch("");
      countryPickerCloseTimerRef.current = null;
    }, 140);
  }

  function applyCurrentNodeCountry(value) {
    if (!countryPicker?.field) return;
    updateCurrentNode({ [countryPicker.field]: value || "" });
    closeCountryPicker();
  }

  function CountryPickerTrigger({ value, placeholder = "", onClick }) {
    const countryName = value ? formatCountryFull(value) : "";
    const displayValue = value ? toIso3(value) || String(value).toUpperCase() : placeholder;
    return (
      <button
        type="button"
        className={`kycClientOwnAssistantCountryTrigger${value ? "" : " is-empty"}`}
        onMouseDown={(e) => {
          e.preventDefault();
          onClick?.();
        }}
        onClick={(e) => {
          if (e.detail === 0) onClick?.();
        }}
      >
        <span className="kycClientOwnAssistantCountryTriggerValue">
          {value ? (
            <CircleFlag
              className="kycMobilePickerFlag"
              countryCode={String(value || "").toLowerCase()}
              height="18"
            />
          ) : null}
          <span>{displayValue}</span>
        </span>
        <i className="bi bi-chevron-down" aria-hidden="true" />
        {value ? (
          <span className="kycClientOwnAssistantCountryTooltip">
            {countryName}
          </span>
        ) : null}
      </button>
    );
  }

  function titleForCurrentNode() {
    if (!currentPath) return "";
    const depth = Math.max(0, currentPath.length - 1);
    const idx = currentPath[currentPath.length - 1] + 1;
    const siblings = getGuidedSiblings(roots, currentPath);
    const total = siblings.length;
    if (depth === 0) {
      return total > 1
        ? `${entityName} | Direct shareholder ${idx} of ${total}`
        : `${entityName} | Direct shareholder`;
    }
    const parent = getGuidedNodeAtPath(roots, currentPath.slice(0, -1));
    const pn = String(parent?.name || "this entity").trim();
    const parentNameDisplay = pn.charAt(0).toUpperCase() + pn.slice(1);
    return total > 1
      ? `${parentNameDisplay} | Shareholder ${idx} of ${total}`
      : `${parentNameDisplay} | Shareholder`;
  }

  // "direct shareholder of PSG A LLC" / "shareholder of Coney Glen Holdings Limited".
  function shareholderOfForCurrentNode() {
    if (!currentPath) return "";
    if (currentPath.length === 1) return `direct shareholder of ${entityName}`;
    const parent = getGuidedNodeAtPath(roots, currentPath.slice(0, -1));
    return `shareholder of ${String(parent?.name || "this entity").trim()}`;
  }

  function currentNodeUsesPercent() {
    if (!currentPath) return false;
    if (currentPath.length === 1) return assistantPercentShouldApply(assistantMode);
    const parent = getGuidedNodeAtPath(roots, currentPath.slice(0, -1));
    return !entityNodeSkipsPercent(
      String(parent?.entityCategory || ""),
      String(parent?.holdingKind || "")
    );
  }

  function getCurrentPercentMax() {
    if (!currentPath) return 100;
    const siblings = getGuidedSiblings(roots, currentPath);
    const currentIdx = currentPath[currentPath.length - 1];
    const usedByOtherOwners = siblings.reduce((sum, sibling, siblingIdx) => {
      if (siblingIdx === currentIdx) return sum;
      return sum + toPercentNumber(sibling?.ownershipPercent);
    }, 0);
    return Math.max(0, Math.round((100 - usedByOtherOwners) * 100) / 100);
  }

  function commitCurrentPercent(rawValue = percentRaw) {
    if (!currentPath) return;
    const nextValue = clampPercentInputValue(rawValue, getCurrentPercentMax());
    setPercentRaw(nextValue);
    updateCurrentNode({ ownershipPercent: nextValue });
  }

  function buildGuidedOwnershipPayload() {
    const rootsWithIds = cloneGuidedNodes(roots).map((node) => ({
      ...node,
      _generatedRowId: makeRowId("sh"),
    }));

    const shareholders = rootsWithIds.map((node, idx) => {
      const type = normalizeGuidedType(node?.type);
      const holderType =
        type === "individual"
          ? "Individual"
          : type === "trust"
          ? "Trust"
          : type === "foundation"
          ? "Foundation"
          : "Entity";
      return {
        _rowId: node._generatedRowId,
        treeNodeId: guidedNodeId(node),
        ...nameFields(node),
        name: String(node?.name || "").trim(),
        holderType,
        beneficialOwnerType: "Beneficial owner",
        nationality: type === "individual" ? String(node?.nationality || "") : "",
        countryOfResidence: String(node?.countryOfResidence || ""),
        ownershipPercent: assistantPercentShouldApply(assistantMode)
          ? getGuidedPercentForNode(rootsWithIds, [idx], node)
          : "",
        shareholderEntityType:
          node?.entityCategory === "pic"
            ? "PIC"
            : node?.entityCategory === "holding"
            ? node?.holdingKind === "passive"
              ? "HoldingPassive"
              : "HoldingActive"
            : node?.entityCategory === "quoted"
            ? "Quoted Company"
            : "Entity",
        isPIC: node?.entityCategory === "pic",
        isHoldingPassive:
          node?.entityCategory === "holding" && node?.holdingKind === "passive",
        isHoldingActive:
          node?.entityCategory === "holding" && node?.holdingKind === "active",
        isPicOrPassiveHolding:
          node?.entityCategory === "pic" ||
          (node?.entityCategory === "holding" && node?.holdingKind === "passive"),
      };
    });

    const shareholderByGeneratedId = new Map(
      rootsWithIds.map((node, idx) => [node._generatedRowId, shareholders[idx]])
    );

    const leafRows = collectGuidedLeafRows(rootsWithIds, assistantMode);
    const ubos = leafRows
      .filter(({ node, depth }) => {
        if (depth === 0) return false;
        const type = normalizeGuidedType(node?.type);
        if (type === "individual") return true;
        if (type === "entity" && node?.entityCategory === "quoted") return true;
        return false;
      })
      .map(({ node, rootRowId: shRowId, effectiveOwnershipPercent }) => ({
        _rowId: makeRowId("ubo"),
        treeNodeId: guidedNodeId(node),
        relatedShareholderRowId: shRowId,
        ...nameFields(node),
        name: String(node?.name || "").trim(),
        nationality: String(node?.nationality || ""),
        countryOfResidence: String(node?.countryOfResidence || ""),
        shareholderEntityType:
          normalizeGuidedType(node?.type) === "entity"
            ? "Quoted Company"
            : undefined,
        basisOfControl: "Indirect ownership",
        effectiveOwnershipPercent: String(effectiveOwnershipPercent || ""),
        ownershipPercent: String(effectiveOwnershipPercent || ""),
        meetsThreshold: null,
      }));

    const arrangements = leafRows
      .filter(({ node }) => ["trust", "foundation"].includes(normalizeGuidedType(node?.type)))
      .map(({ node, rootRowId }) => {
        const type = normalizeGuidedType(node?.type);
        const shareholder = shareholderByGeneratedId.get(rootRowId || node._generatedRowId);
        const rawParties = type === "foundation"
          ? (Array.isArray(node?.foundationParties) ? node.foundationParties : makeDefaultFoundationParties())
          : (Array.isArray(node?.trustParties) ? node.trustParties : makeDefaultTrustParties());
        return {
          _rowId: makeRowId("la"),
          arrangementType: type === "foundation" ? "Foundation" : "Trust",
          ...nameFields(node),
          name: String(node?.name || "").trim(),
          source: "shareholder",
          sourceRefRowId: String(shareholder?._rowId || ""),
          relatedShareholderName:
            String(shareholder?.name || "").trim() || entityName,
          autoCreated: true,
          _treeArrangementNodeId: guidedNodeId(node) || String(node?._generatedRowId || ""),
          parties: rawParties.map((p) => ({
            _rowId: p._rowId || makeRowId("laP"),
            role: String(p?.role || ""),
            ...nameFields(p),
            name: String(p?.name || "").trim(),
            trusteeKind: String(p?.trusteeKind || p?.councilKind || ""),
            nationality: String(p?.nationality || ""),
            domicile: String(p?.domicile || ""),
            autoCreated: true,
          })),
        };
      });

    return {
      shareholders,
      ubos,
      arrangements,
      assistantTree: buildGuidedAssistantSnapshot(entityName, roots, assistantMode),
      assistantRoots: rootsWithIds,
      desktopAssistantMode: assistantMode,
    };
  }

  function finishAssistant() {
    const payload = buildGuidedOwnershipPayload();
    onGenerateOwnership(payload);
    setSubmitted(true);
    setOpen(false);
  }

  function closeAssistant() {
    if (!submitted) {
      onExitIncomplete?.();
      return;
    }
    setOpen(false);
  }

  function restartAssistant() {
    setDocumentStart(null);
    setRoots([]);
    setDirectCount("");
    setDirectCountRaw("");
    setShowDirectCountInput(false);
    setOwnersCountRaw("");
    setShowOwnersCountInput(false);
    setNameRaw("");
    setPercentRaw("");
    setPartiesModalOpen(false);
    setCountryPicker(null);
    setCountryPickerClosing(false);
    setCountrySearch("");
    setSubmitted(false);
    setOpen(true);
  }

  function renderTypeOptions() {
    const options = [
      { value: "individual", label: "Individual", hint: "A natural person.", icon: iconIndividual },
      { value: "entity", label: "Entity", hint: "A company or legal entity.", icon: iconEntity },
      { value: "legalArrangement", label: "Legal arrangement", hint: "Trust or foundation.", icon: iconCertificate },
    ];
    return (
      <div className="kycClientOwnAssistantOptionGrid kycClientOwnAssistantOptionGrid--shareholderType">
        {options.map((option) => (
          <button key={option.value} type="button" onClick={() => setCurrentType(option.value)}>
            <span className="kycClientOwnAssistantOptionIcon">
              <img src={option.icon} alt="" aria-hidden="true" />
            </span>
            <span className="kycClientOwnAssistantOptionCopy">
              <strong>{option.label}</strong>
              <span>{option.hint}</span>
            </span>
          </button>
        ))}
      </div>
    );
  }

  function renderGuidedTranscript() {
    const bubbles = [
      {
        speaker: "assistant",
        // Started from the company documents: why the owners of the company shareholders are asked (the number of
        // direct shareholders is in it, so it is not asked).
        text: documentStart
          ? companyShareholdersNotice(documentStart.companies, entityName, documentStart.roots.length)
          : "We will build the ownership chain step by step, then generate the ownership summary for review.",
      },
    ];

    if (directCount && !documentStart) {
      bubbles.push({
        speaker: "assistant",
        text: `How many direct shareholders does ${entityName} have?`,
      });
      bubbles.push({
        speaker: "user",
        text: `${directCount} direct shareholder${Number(directCount) > 1 ? "s" : ""}`,
      });
    }

    function walk(nodes, parentEntityName, isDirect, pathPrefix = []) {
      const nodeList = Array.isArray(nodes) ? nodes : [];
      const total = nodeList.length;
      nodeList.forEach((node, index) => {
        const path = [...pathPrefix, index];
        const isEditingCurrent =
          Array.isArray(currentPath) && currentPath.join(".") === path.join(".");
        const label = isDirect ? "Direct shareholder" : "Shareholder";
        const title = total > 1
          ? `${parentEntityName} | ${label} ${index + 1} of ${total}`
          : `${parentEntityName} | ${label}`;
        const summary = formatGuidedNodeSummary(node, roots, path, assistantMode);

        if (summary && !isEditingCurrent) {
          bubbles.push({ speaker: "assistant", text: title });
          bubbles.push({ speaker: "user", text: summary });
        }

        if (normalizeGuidedType(node?.type) === "entity" && !isEditingCurrent) {
          if (node?.entityCategory === "quoted") {
            bubbles.push({
              speaker: "assistant",
              text: quotedCompanyShareholdersNotRequiredText(node?.name || title),
            });
          } else if (String(node?.ownersCount || "").trim()) {
            const nodeName = String(node?.name || title).trim();
            bubbles.push({
              speaker: "assistant",
              text: `How many shareholders does ${nodeName.charAt(0).toUpperCase() + nodeName.slice(1)} have?`,
            });
            bubbles.push({
              speaker: "user",
              text: `${node.ownersCount} owner${
                Number.parseInt(String(node.ownersCount), 10) > 1 ? "s" : ""
              }`,
            });
          }
        }

        if (Array.isArray(node?.owners) && node.owners.length) {
          const childParentName = String(node?.name || title).trim();
          walk(node.owners, childParentName.charAt(0).toUpperCase() + childParentName.slice(1), false, path);
        }
      });
    }

    walk(roots, entityName, true);

    return (
      <div className="kycClientOwnAssistantTranscript">
        {bubbles.map((bubble, index) => (
          <div
            key={`guided_transcript_${index}`}
            className={`kycClientOwnAssistantBubble is-${bubble.speaker}`}
          >
            {ownershipLabel(bubble.text)}
          </div>
        ))}
      </div>
    );
  }

  function renderEntityCategoryOptions() {
    const options = [
      ["nonQuotedOperational", "Operating company", "Carries out commercial business activities and is not listed."],
      ["holding", "Holding company", "Primarily holds interests in companies or assets."],
      ["pic", "Domiciliary company (PIC)", "Used to hold assets or investments."],
      ["quoted", "Quoted company", "Listed on a regulated stock exchange."],
    ];
    return (
      <div className="kycClientOwnAssistantOptionGrid">
        {options.map(([value, label, hint]) => (
          <button
            key={value}
            type="button"
            onClick={() =>
              updateCurrentNode({
                entityCategory: value,
                holdingKind: "",
                ownersCount: value === "quoted" ? "0" : "",
                owners: value === "quoted" ? [] : currentNode?.owners || [],
              })
            }
          >
            <strong>{ownershipLabel(label)}</strong>
            <span>{hint}</span>
          </button>
        ))}
      </div>
    );
  }

  function renderCurrentPrompt() {
    if (!directCount) {
      return (
        <>
          <h6>Direct shareholders</h6>
          <p>How many direct shareholders does {entityName} have?</p>
          <div className="kycClientOwnAssistantNumberGrid">
            {Array.from({ length: 10 }, (_, index) => index + 1).map((value) => (
              <button
                key={`direct_count_${value}`}
                type="button"
                onClick={() => setRootCount(value)}
              >
                {value}
              </button>
            ))}
            <button
              type="button"
              className="kycClientOwnAssistantNumberOther"
              onClick={() => setShowDirectCountInput(true)}
            >
              ...
            </button>
          </div>
          {showDirectCountInput ? (
            <div className="kycClientOwnAssistantNumberCustom">
              <input
                type="text"
                inputMode="numeric"
                className="form-control"
                value={directCountRaw}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 2);
                  setDirectCountRaw(raw);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const n = parseInt(directCountRaw, 10);
                    if (n >= 1 && n <= 99) setRootCount(directCountRaw);
                  }
                }}
                placeholder="Enter number of shareholders"
              />
              <button
                type="button"
                className="kycClientOwnTextBtn"
                onClick={() => {
                  const n = parseInt(directCountRaw, 10);
                  if (n >= 1 && n <= 99) setRootCount(directCountRaw);
                }}
              >
                Confirm
              </button>
            </div>
          ) : null}
        </>
      );
    }

    if (!currentPath || !currentNode) {
      return (
        <div className="kycClientOwnAssistantCompletedPrompt">
          <h6>Ownership assistant completed</h6>
          <p>
            All ownership details have been captured. Generate the ownership
            summary and review the editable rows below.
          </p>
          <button
            type="button"
            className="kycClientOwnPrimaryBtn kycClientOwnAssistantProceedBtn"
            onClick={finishAssistant}
          >
            Proceed to ownership summary
          </button>
        </div>
      );
    }

    if (!currentType) {
      return (
        <>
          <h6>{titleForCurrentNode()}</h6>
          <p>Select type of shareholder :</p>
          {renderTypeOptions()}
        </>
      );
    }

    if (currentType === "legalArrangement") {
      return (
        <>
          <h6>{titleForCurrentNode()}</h6>
          <p>Select type of legal arrangement.</p>
          <div className="kycClientOwnAssistantOptionGrid">
            <button type="button" onClick={() => setCurrentType("trust")}>
              <strong>Trust</strong>
            </button>
            <button type="button" onClick={() => setCurrentType("foundation")}>
              <strong>Foundation</strong>
            </button>
          </div>
        </>
      );
    }

    if (currentType === "entity" && !currentNode?.entityCategory) {
      return (
        <>
          {/* Named already (company documents): one line naming it and whose shareholder it is, no title. */}
          {String(currentNode?.name || "").trim() ? (
            <p className="kycClientOwnAssistantNamedQuestion">
              What type of company is <strong>{String(currentNode.name).trim()}</strong> ({shareholderOfForCurrentNode()})?
            </p>
          ) : (
            <>
              <h6>{titleForCurrentNode()}</h6>
              <p>What type of entity is this?</p>
            </>
          )}
          {renderEntityCategoryOptions()}
        </>
      );
    }

    if (currentType === "entity" && currentNode?.entityCategory === "holding" && !currentNode?.holdingKind) {
      return (
        <>
          <h6>{titleForCurrentNode()}</h6>
          <p>Is this an active or passive holding?</p>
          <div className="kycClientOwnAssistantOptionGrid">
            <button type="button" onClick={() => updateCurrentNode({ holdingKind: "active" })}>
              <strong>Active holding</strong>
              <span>Holds majority stakes in operating companies.</span>
            </button>
            <button type="button" onClick={() => updateCurrentNode({ holdingKind: "passive" })}>
              <strong>Passive holding</strong>
              <span>Mainly combines and manages assets.</span>
            </button>
          </div>
        </>
      );
    }

    const usesPercent = currentNodeUsesPercent();
    const siblings = getGuidedSiblings(roots, currentPath);
    const autoPercent = usesPercent && siblings.length <= 1;
    const isLast = currentPath[currentPath.length - 1] === siblings.length - 1;
    const currentPercent = getGuidedPercentForNode(roots, currentPath, currentNode);
    const isAutoLastPercent = usesPercent && siblings.length > 1 && isLast;
    const currentPercentMax = usesPercent ? getCurrentPercentMax() : 100;

    return (
      <>
        <h6>{titleForCurrentNode()}</h6>
        <p>{isLast ? "Complete this shareholder's details before moving to the next branch" : "Complete this shareholder's details before moving to the next one"}</p>

        <div
          className={`kycClientOwnAssistantFields${
            currentType === "entity" ? " kycClientOwnAssistantFields--entity" : ""
          }${
            currentType === "individual" && usesPercent
              ? " kycClientOwnAssistantFields--individualWithPercent"
              : ""
          }${
            currentType === "entity" && usesPercent
              ? " kycClientOwnAssistantFields--entityWithPercent"
              : ""
          }`}
        >
          {currentType === "individual" ? (
            <>
              <div
                className={`kycInfoItem kycInfoItemAlignTop kycClientOwnAssistantInfoField kycClientOwnAssistantIndividualNameField${
                  usesPercent ? "" : " kycClientOwnAssistantFields--fullrow"
                }`}
              >
                <div className="kycInfoIconDarkBeige">
                  <img src={iconIndividual} alt="" className="kycInfoIconImg" />
                </div>
                <div className="kycInfoContent">
                  <div className="kycInfoViewLabel">Full name</div>
                  {separateNames ? (
                    <IndividualNamePreview
                      key={currentNode?._rowId || currentPath.join(".")}
                      inputClass="form-control kycCustomerDetailsHybridInput--line kycClientOwnAssistantLineInput"
                      displayName={nameRaw}
                      value={currentNode} onChange={(patch) => { setNameRaw(patch.name); updateCurrentNode(patch); }}
                    />
                  ) : (
                  <input
                    className="form-control kycCustomerDetailsHybridInput--line kycClientOwnAssistantLineInput"
                    value={nameRaw}
                    onChange={(e) => {
                      const nextName = toTitleCase(e.target.value);
                      setNameRaw(nextName);
                    }}
                    onBlur={() => updateCurrentNode({ name: nameRaw })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") updateCurrentNode({ name: nameRaw });
                    }}
                  />
                  )}
                </div>
              </div>
              <div className="kycInfoItem kycInfoItemAlignTop kycClientOwnAssistantInfoField kycClientOwnAssistantNationalityField">
                <div className="kycInfoIconLightGrey">
                  <img src={iconPassport} alt="" className="kycInfoIconImg" />
                </div>
                <div className="kycInfoContent">
                  <div className="kycInfoViewLabel">Nationality</div>
                  <CountryPickerTrigger
                    value={currentNode?.nationality || ""}
                    onClick={() => openCurrentNodeCountryPicker("nationality", "Nationality")}
                  />
                </div>
              </div>
              <div className="kycInfoItem kycInfoItemAlignTop kycClientOwnAssistantInfoField kycClientOwnAssistantDomicileField">
                <div className="kycInfoIconLightGrey">
                  <img src={iconAddress} alt="" className="kycInfoIconImg" />
                </div>
                <div className="kycInfoContent">
                  <div className="kycInfoViewLabel">Domicile</div>
                  <CountryPickerTrigger
                    value={currentNode?.countryOfResidence || ""}
                    onClick={() => openCurrentNodeCountryPicker("countryOfResidence", "Domicile")}
                  />
                </div>
              </div>
            </>
          ) : currentType === "entity" ? (
            <>
              <div
                className={`kycInfoItem kycInfoItemAlignTop kycClientOwnAssistantInfoField kycClientOwnAssistantEntityNameField${
                  usesPercent ? " kycClientOwnAssistantFields--fullrow" : ""
                }`}
              >
                <div className="kycInfoIconDarkBeige">
                  <img src={iconEntity} alt="" className="kycInfoIconImg" />
                </div>
                <div className="kycInfoContent">
                  <div className="kycInfoViewLabel">Entity name</div>
                  <input
                    className="form-control kycCustomerDetailsHybridInput--line kycClientOwnAssistantLineInput"
                    value={nameRaw}
                    onChange={(e) => {
                      const nextName = toTitleCase(e.target.value);
                      setNameRaw(nextName);
                    }}
                    onBlur={() => updateCurrentNode({ name: nameRaw })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") updateCurrentNode({ name: nameRaw });
                    }}
                  />
                </div>
              </div>

              <div className="kycInfoItem kycInfoItemAlignTop kycClientOwnAssistantInfoField">
                <div className="kycInfoIconLightGrey">
                  <img src={iconAddress} alt="" className="kycInfoIconImg" />
                </div>
                <div className="kycInfoContent">
                  <div className="kycInfoViewLabel">Country</div>
                  <CountryPickerTrigger
                    value={currentNode?.countryOfResidence || ""}
                    onClick={() =>
                      openCurrentNodeCountryPicker(
                        "countryOfResidence",
                        "Country of incorporation"
                      )
                    }
                  />
                </div>
              </div>
            </>
          ) : (
            <label>
              <span>
                {currentType === "trust"
                  ? "Trust name"
                  : currentType === "foundation"
                  ? "Foundation name"
                  : "Full name"}
              </span>
              <input
                className="form-control"
                value={nameRaw}
                onChange={(e) => {
                  const nextName = toTitleCase(e.target.value);
                  setNameRaw(nextName);
                }}
                onBlur={() => updateCurrentNode({ name: nameRaw })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") updateCurrentNode({ name: nameRaw });
                }}
              />
            </label>
          )}

          {usesPercent ? (
            <div
              className={`kycInfoItem kycInfoItemAlignTop kycClientOwnAssistantInfoField${
                currentType === "individual" ? " kycClientOwnAssistantPercentField" : ""
              }`}
            >
              <div className="kycInfoIconLightGrey kycClientOwnAssistantOwnershipIcon">
                <img src={iconPercent} alt="" className="kycInfoIconImg" />
              </div>
              <div className="kycInfoContent">
                <div className="kycInfoViewLabel">Ownership %</div>
                <input
                  className="form-control kycCustomerDetailsHybridInput--line kycClientOwnAssistantLineInput kycClientOwnAssistantPercentInput"
                  type="text"
                  inputMode="decimal"
                  min="0"
                  max={currentPercentMax}
                  value={autoPercent || isAutoLastPercent ? currentPercent : percentRaw}
                  disabled={autoPercent || isAutoLastPercent}
                  onChange={(e) => setPercentRaw(clampPercentInputValue(e.target.value, currentPercentMax))}
                  onBlur={() => commitCurrentPercent()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitCurrentPercent();
                  }}
                  placeholder={`0-${formatPercentInputValue(currentPercentMax)}`}
                />
              </div>
            </div>
          ) : null}

          {(currentType === "trust" || currentType === "foundation") ? (() => {
            const collection = currentType === "trust" ? "trustParties" : "foundationParties";
            const parties = Array.isArray(currentNode?.[collection]) ? currentNode[collection] : [];
            const savedKey = currentType === "trust" ? "trustPartiesSaved" : "foundationPartiesSaved";
            const complete = parties.length > 0 && parties.every(
              currentType === "trust" ? isTrustPartyComplete : isFoundationPartyComplete
            );
            const saved = !!currentNode?.[savedKey] && complete;
            return (
              <div className="kycClientOwnAssistantFields--fullrow">
                <button
                  type="button"
                  className={`kycClientOwnPartiesBtn${saved ? " is-complete" : ""}`}
                  onClick={() => {
                    if (!parties.length) {
                      updateCurrentNode({
                        [collection]: currentType === "trust"
                          ? makeDefaultTrustParties()
                          : makeDefaultFoundationParties(),
                        [savedKey]: false,
                      });
                    }
                    setPartiesModalOpen(true);
                  }}
                >
                  <span>
                    {saved
                      ? parties.map((p) => String(p?.name || "").trim()).filter(Boolean).join(", ")
                      : `Enter ${currentType} party details…`}
                  </span>
                  <i className="bi bi-pencil-square" aria-hidden="true" />
                </button>
              </div>
            );
          })() : null}

          {currentType === "entity" && currentNode?.entityCategory !== "quoted" ? (
            <label className="kycClientOwnAssistantFields--fullrow">
              <span>
                How many shareholders does{" "}
                {currentNode?.name
                  ? String(currentNode.name).replace(/^./, (c) => c.toUpperCase())
                  : "this entity"}{" "}
                have?
              </span>
              <div className="kycClientOwnAssistantNumberGrid">
                {Array.from({ length: 10 }, (_, index) => index + 1).map((value) => (
                  <button
                    key={`owners_count_${currentPathKey}_${value}`}
                    type="button"
                    className={
                      String(currentNode?.ownersCount || "") === String(value)
                        ? "is-selected"
                        : ""
                    }
                    onClick={() => setCurrentOwnersCount(value)}
                  >
                    {value}
                  </button>
                ))}
                <button
                  type="button"
                  className="kycClientOwnAssistantNumberOther"
                  onClick={() => setShowOwnersCountInput(true)}
                >
                  ...
                </button>
              </div>
              {showOwnersCountInput ? (
                <div className="kycClientOwnAssistantNumberCustom">
                  <input
                    className="form-control"
                    type="text"
                    inputMode="numeric"
                    value={ownersCountRaw}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 2);
                      setOwnersCountRaw(raw);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const n = parseInt(ownersCountRaw, 10);
                        if (n >= 1 && n <= 99) setCurrentOwnersCount(ownersCountRaw);
                      }
                    }}
                    placeholder="Enter number of shareholders"
                  />
                  <button
                    type="button"
                    className="kycClientOwnTextBtn"
                    onClick={() => {
                      const n = parseInt(ownersCountRaw, 10);
                      if (n >= 1 && n <= 99) setCurrentOwnersCount(ownersCountRaw);
                    }}
                  >
                    Confirm
                  </button>
                </div>
              ) : null}
            </label>
          ) : null}
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className={`card kycCard mb-3 kycClientOwnActionCard ${
          submitted ? "kycClientOwnActionCard--summary" : ""
        }`}
      >
        <div className="kycClientOwnActionCardMain">
          <div className={submitted ? "kycClientOwnGeneratedHeaderText" : ""}>
            <div className="kycClientOwnEyebrow">Guided assistant</div>
            <strong>
              {submitted ? "Ownership summary generated" : "Build ownership step by step"}
            </strong>
            <p>
              {submitted
                ? "Review and edit the generated ownership details below :"
                : "Use a desktop-friendly interview flow with normal keyboard inputs."}
            </p>
          </div>
          <button type="button" className="kycClientOwnPrimaryBtn" onClick={submitted ? restartAssistant : () => setOpen(true)}>
            {submitted ? "Restart assistant" : "Open guided assistant"}
          </button>
        </div>

        {submitted && summaryContent ? (
          <div className="kycClientOwnGeneratedSummary">{summaryContent}</div>
        ) : null}
      </div>

      {open ? (
        <div
          className="kycClientOwnAssistantOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={closeAssistant}
        >
          <div
            className="kycClientOwnAssistantModal"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="kycClientOwnAssistantHeader">
              <div>
                <div className="kycClientOwnEyebrow">Step-by-step guide</div>
                <h6>Guided Ownership Assistant</h6>
              </div>
              <button type="button" className="kycClientOwnTextBtn" onClick={closeAssistant}>
                Close
              </button>
            </div>

            <div className="kycClientOwnAssistantBody">
              <div className="kycClientOwnAssistantTreePane">
                <div className="kycClientOwnAssistantTreeHead">
                  <div className="kycClientOwnEyebrow">Live ownership structure preview</div>
                  <strong>{entityName}</strong>
                </div>
                <div className="kycClientOwnAssistantTreeCanvas">
                  <OwnershipTreeUaeReadOnly snapshot={liveAssistantSnapshot} edgeLabelBg="#fffcfb" highlightNodeId={liveHighlightNodeId} />
                </div>
              </div>

              <div className="kycClientOwnAssistantChatPane">
                <div className="kycClientOwnAssistantChatScroll" ref={chatPaneRef}>
                  {renderGuidedTranscript()}

                  <div className="kycClientOwnAssistantBubble is-assistant is-prompt">
                    <div className="kycClientOwnAssistantPrompt">
                      {renderCurrentPrompt()}
                    </div>
                  </div>
                </div>

                {countryPicker ? (
                  <div
                    className={`kycClientOwnAssistantCountryLayer${
                      countryPickerClosing ? " is-closing" : ""
                    }`}
                    onMouseDown={closeCountryPicker}
                  >
                    <div
                      className={`kycClientOwnAssistantCountrySheet${
                        countryPickerClosing ? " is-closing" : ""
                      }`}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="kycClientOwnAssistantCountrySheetHeader">
                        <button
                          type="button"
                          className="kycClientOwnAssistantCountrySheetCancel"
                          onClick={closeCountryPicker}
                        >
                          Cancel
                        </button>
                        <div className="kycClientOwnAssistantCountrySheetTitle">
                          {countryPicker.title || "Country"}
                        </div>
                        <div className="kycClientOwnAssistantCountrySheetSpacer" />
                      </div>

                      <div className="kycClientOwnAssistantCountrySearchWrap">
                        <i className="bi bi-search" aria-hidden="true" />
                        <input
                          ref={countrySearchRef}
                          autoFocus
                          className="kycClientOwnAssistantCountrySearchInput"
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (
                              e.key === "Enter" &&
                              filteredCountryOptions.length === 1
                            ) {
                              e.preventDefault();
                              applyCurrentNodeCountry(filteredCountryOptions[0].value);
                            }
                            if (e.key === "Escape") {
                              e.preventDefault();
                              closeCountryPicker();
                            }
                          }}
                          placeholder="Search country"
                        />
                      </div>

                      <div className="kycClientOwnAssistantCountryList">
                        {filteredCountryOptions.map((opt) => {
                          const isSelected = opt.value === countryPicker.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              className={`kycClientOwnAssistantCountryOption${
                                isSelected ? " is-selected" : ""
                              }`}
                              onClick={() => applyCurrentNodeCountry(opt.value)}
                            >
                              <span className="kycClientOwnAssistantCountryOptionLeft">
                                <CircleFlag
                                  className="kycMobilePickerFlag"
                                  countryCode={String(opt.value || "").toLowerCase()}
                                  height="18"
                                />
                                <span>{opt.label}</span>
                              </span>
                              {isSelected ? (
                                <i className="bi bi-check2" aria-hidden="true" />
                              ) : (
                                <i className="bi bi-chevron-right" aria-hidden="true" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            {partiesModalOpen && currentNode && (currentType === "trust" || currentType === "foundation") ? (() => {
              const isTrust = currentType === "trust";
              const collection = isTrust ? "trustParties" : "foundationParties";
              const parties = Array.isArray(currentNode[collection]) ? currentNode[collection] : [];
              const isComplete = parties.length > 0 && parties.every(isTrust ? isTrustPartyComplete : isFoundationPartyComplete);
              const sortOrder = isTrust
                ? { Settlor: 0, Trustee: 1, Protector: 2, Beneficiary: 3 }
                : { Founder: 0, "Council member": 1, Guardian: 2, Beneficiary: 3 };
              const sorted = [...parties].sort((a, b) => (sortOrder[a.role] ?? 99) - (sortOrder[b.role] ?? 99));

              return (
                <div className="kycClientOwnPartiesModalOverlay" onMouseDown={() => setPartiesModalOpen(false)}>
                  <div className="kycClientOwnPartiesModal" onMouseDown={(e) => e.stopPropagation()}>

                    <div className="kycClientOwnPartiesModalHeader">
                      <div className="kycClientOwnPartiesModalHeaderMain">
                        <div className="kycClientOwnPartiesModalLottieWrap">
                          <Lottie animationData={trustBadgeLottieJson} loop autoplay className="kycClientOwnPartiesModalLottie" />
                        </div>
                        <div className="kycClientOwnPartiesModalHeaderText">
                          <div className="kycClientOwnPartiesModalTitle">
                            {isTrust ? "Trust details" : "Foundation details"}
                          </div>
                          <div className="kycClientOwnPartiesModalHint">
                            {isTrust ? "Complete the trust parties below." : "Complete the foundation parties below."}
                          </div>
                        </div>
                      </div>
                      <button type="button" className="kycClientOwnPartiesModalClose" onClick={() => setPartiesModalOpen(false)} aria-label="Close">
                        <i className="bi bi-x-lg" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="kycClientOwnPartiesModalBody">
                      {sorted.map((party, pIdx) => {
                        const role = String(party?.role || "");
                        const isKindRole = (isTrust && role === "Trustee") || (!isTrust && role === "Council member");
                        const kindKey = isTrust ? "trusteeKind" : "councilKind";
                        const kind = String(party?.[kindKey] || "").toLowerCase();
                        const canDelete = role === "Beneficiary" || role === "Protector" || role === "Guardian";
                        const beneficiaryCount = parties.filter((p) => p.role === "Beneficiary").length;
                        const beneficiaryIndex = role === "Beneficiary"
                          ? sorted.filter((p) => p.role === "Beneficiary").findIndex((p) => p._rowId === party._rowId) + 1
                          : 0;
                        const roleLabel = role === "Beneficiary" ? `Beneficiary ${beneficiaryIndex}` : role;
                        const isFirst = pIdx === 0;

                        const partyIcon =
                          role === "Settlor" || role === "Founder"
                            ? <img src={iconSettlor} alt="" className="kycClientOwnPartyRailIcon" />
                            : role === "Trustee" || role === "Council member"
                            ? <img src={iconHandshake} alt="" className="kycClientOwnPartyRailIcon" />
                            : role === "Beneficiary"
                            ? <img src={iconBeneficiary} alt="" className="kycClientOwnPartyRailIcon" />
                            : <i className="bi bi-shield-check kycClientOwnPartyRailIconBi" aria-hidden="true" />;

                        return (
                          <div key={party._rowId} className={`kycClientOwnPartyBlock${isFirst ? " is-first" : ""}`}>
                            <div className="kycClientOwnPartyBlockHeader">
                              <span className="kycClientOwnPartyRole">{roleLabel}</span>
                              {canDelete && (beneficiaryCount > 1 || role !== "Beneficiary") ? (
                                <button
                                  type="button"
                                  className="kycClientOwnPartyRemoveBtn"
                                  onClick={() => removeCurrentNodeParty(collection, party._rowId)}
                                  aria-label={`Remove ${roleLabel}`}
                                >
                                  <i className="bi bi-trash3" aria-hidden="true" />
                                </button>
                              ) : null}
                            </div>

                            <div className="kycClientOwnPartyCard">
                              <div className="kycClientOwnPartyRail">
                                {partyIcon}
                              </div>
                              <div className="kycClientOwnPartyCardContent">
                                {isKindRole && (
                                  <div className="kycClientOwnPartyKindRow">
                                    <span className="kycClientOwnPartyKindLabel">Type</span>
                                    <div className="kycClientOwnPartyKindBtns">
                                      {["individual", "entity"].map((k) => (
                                        <button
                                          key={k}
                                          type="button"
                                          className={`kycClientOwnPartyKindBtn${kind === k ? " is-active" : ""}`}
                                          onClick={() => updateCurrentNodeParty(collection, party._rowId, { [kindKey]: k })}
                                        >
                                          {k.charAt(0).toUpperCase() + k.slice(1)}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                <label className="kycClientOwnPartyField">
                                  <span>
                                    {isKindRole
                                      ? kind === "entity" ? `Name of corporate ${role.toLowerCase()}` : `Name of ${role.toLowerCase()}`
                                      : "Name"}
                                  </span>
                                  {separateNames && (!isKindRole || kind === "individual") ? (
                                    <IndividualNamePreview
                                      inputClass="form-control"
                                      displayName={party?.name || ""}
                                      value={party} onChange={(patch) => updateCurrentNodeParty(collection, party._rowId, patch)}
                                      framed
                                    />
                                  ) : (
                                  <input
                                    className="form-control"
                                    value={toTitleCase(party?.name || "")}
                                    onChange={(e) => updateCurrentNodeParty(collection, party._rowId, { name: toTitleCase(e.target.value) })}
                                    placeholder={isKindRole && !["individual","entity"].includes(kind) ? "Select individual or entity first" : ""}
                                    disabled={isKindRole && !["individual","entity"].includes(kind)}
                                  />
                                  )}
                                </label>

                                {(!isKindRole || kind === "individual") && (
                                  <label className="kycClientOwnPartyField kycClientOwnPartyFieldWithSideIcon">
                                    <span className="kycClientOwnPartyFieldSideBadge">
                                      <img
                                        src={iconPassport}
                                        alt=""
                                        aria-hidden="true"
                                        className="kycClientOwnPartyFieldSideIcon"
                                      />
                                    </span>
                                    <span className="kycClientOwnPartyFieldSideContent">
                                      <span className="kycClientOwnPartyFieldSideTitle">Nationality</span>
                                      <CountrySelect
                                        value={party?.nationality || ""}
                                        onChange={(value) => updateCurrentNodeParty(collection, party._rowId, { nationality: value || "" })}
                                        optionFontSize={13}
                                      />
                                    </span>
                                  </label>
                                )}

                                <label className="kycClientOwnPartyField kycClientOwnPartyFieldWithSideIcon">
                                  <span className="kycClientOwnPartyFieldSideBadge">
                                    <img
                                      src={iconAddress}
                                      alt=""
                                      aria-hidden="true"
                                      className="kycClientOwnPartyFieldSideIcon"
                                    />
                                  </span>
                                  <span className="kycClientOwnPartyFieldSideContent">
                                    <span className="kycClientOwnPartyFieldSideTitle">Domicile</span>
                                    <CountrySelect
                                      value={party?.domicile || ""}
                                      onChange={(value) => updateCurrentNodeParty(collection, party._rowId, { domicile: value || "" })}
                                      optionFontSize={13}
                                    />
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      <div className="kycClientOwnPartyAddRow">
                        <button type="button" className="kycClientOwnPartyAddBtn" onClick={() => addCurrentNodeParty(collection, "Beneficiary")}>
                          <i className="bi bi-people me-1" aria-hidden="true" /> Add beneficiary
                        </button>
                        {!parties.some((p) => p.role === (isTrust ? "Protector" : "Guardian")) && (
                          <button type="button" className="kycClientOwnPartyAddBtn" onClick={() => addCurrentNodeParty(collection, isTrust ? "Protector" : "Guardian")}>
                            <i className="bi bi-shield-plus me-1" aria-hidden="true" /> Add {isTrust ? "protector" : "guardian"}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="kycClientOwnPartiesModalFooter">
                      <button
                        type="button"
                        className={`kycClientOwnPartiesModalSave${isComplete ? "" : " is-blocked"}`}
                        disabled={!isComplete}
                        onClick={() => saveCurrentNodeParties(collection)}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              );
            })() : null}
          </div>
        </div>
      ) : null}
    </>
  );
}


export default function KycClientOwnershipDesktop({
  sectionNo = 0,
  separateNames = false,
  form,
  orgJurisdiction = null,
  onSetAt,
  changedClass,
  readOnly = false,
  submitAttempted = false,
  mode = "client",
  showGuidedMethod = true,
  // Staff form: visual builder + manual entry; client e-form (kyc-sent): guided assistant + visual builder.
  showManualEntryMethod = false,
  // Client e-form (kyc-sent): client wording in the ownership change dialogs.
  clientFacing = false,
  forceApiCallInTree = false,
  skipTutorial = false,
  // kyc-sent: the client entity type was answered in the company upload-first step, so it is not asked
  // again here (it stays changeable there).
  entityTypeAnsweredEarlier = false,
  // kyc-sent: while the upload-first card is on screen, the structure question waits.
  structurePromptPaused = false,
  onVisualBuilderModalOpenChange,
}) {
  const { ownershipLabel } = useOwnershipLabels(orgJurisdiction);
  const ownership = form?.ownership || {};
  const ownershipHasShareholders = [ownership.shareholders, ownership.directShareholders]
    .some((list) => Array.isArray(list) && list.some((row) => String(row?.name || "").trim()));
  const [structureModalDismissed, setStructureModalDismissed] = useState(false);
  // Entity type answered earlier: shown as one line; "Change" brings the usual selector back.
  const entityName = String(form?.customer?.name || "").trim() || "the client";
  const flags = ownership?.uboFlags || {};
  const structureType = String(ownership?.desktopClientStructureType || "");
  const mobileEntryMode = String(ownership?.mobileEntryMode || "").trim();
  const mobilePreliminaryMode = String(
    ownership?.mobileAssistantDraft?.preliminaryMode || ""
  ).trim();

  const initialProfile = useMemo(() => {
    const savedProfile = String(ownership?.desktopClientEntityType || "").trim();
    if (savedProfile) return savedProfile;
    if (flags.quotedCompany) return "quoted";
    if (flags.accountHolderTrust) return "trust";
    if (flags.accountHolderFoundation) return "foundation";
    if (flags.pctNotMeaningful) return "pic";
    if (mobilePreliminaryMode === "quoted") return "quoted";
    if (mobilePreliminaryMode === "trust") return "trust";
    if (mobilePreliminaryMode === "foundation") return "foundation";
    if (mobilePreliminaryMode === "pic") return "pic";
    if (
      mobilePreliminaryMode === "holding" ||
      mobilePreliminaryMode === "holdingActive" ||
      mobilePreliminaryMode === "holdingPassive"
    ) {
      return "holding";
    }
    if (mobilePreliminaryMode === "noneOfAbove") return "operating";
    return "";
  }, [
    ownership?.desktopClientEntityType,
    flags.quotedCompany,
    flags.accountHolderTrust,
    flags.accountHolderFoundation,
    flags.pctNotMeaningful,
    mobilePreliminaryMode,
  ]);

  const [profile, setProfile] = useState(initialProfile);
  // Client-type change once ownership details exist (keep the structure / start over).
  const [typeChange, setTypeChange] = useState(null);
  const [keepRowsToken, setKeepRowsToken] = useState(0);
  // Holding card clicked with ownership details: active/passive is asked first, then the dialog.
  const [pendingHoldingChoice, setPendingHoldingChoice] = useState(false);
  // Percentages became required after a type change (note until all are entered).
  const [percentsNeeded, setPercentsNeeded] = useState(false);
  const { requestRemoval, removalDialog } = useOwnershipRemoval(form);
  const [holdingKind, setHoldingKind] = useState(
    String(ownership?.desktopClientHoldingKind || "")
  );
  const [trustNameKind, setTrustNameKind] = useState(
    String(ownership?.accountHolderTrustResolution?.kind || "")
  );
  const savedCompletionMethod = String(ownership?.desktopClientCompletionMethod || "");
  const [method, setMethod] = useState(
    String(ownership?.desktopClientCompletionMethod || "")
  );
  const [tutorialVisible, setTutorialVisible] = useState(!skipTutorial);
  const [entityHintLineCounts, setEntityHintLineCounts] = useState({});
  const [methodHintLineCounts, setMethodHintLineCounts] = useState({});
  const [builderOpenKey, setBuilderOpenKey] = useState("");
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [visualBuilderCompleted, setVisualBuilderCompleted] = useState(false);
  const [visualBuilderLaunching, setVisualBuilderLaunching] = useState(false);
  const [uboModalOpen, setUboModalOpen] = useState(false);
  const [uboApiLoading, setUboApiLoading] = useState(false);
  const [uboApiError, setUboApiError] = useState("");
  const [uboApiResult, setUboApiResult] = useState(null);
  const initialOwnershipDebugLoggedRef = useRef(false);

  const selectedVisualType = visualBuilderType(profile, holdingKind);
  const isTerminalAccountHolder =
    profile === "quoted" || profile === "trust" || profile === "foundation";
  const savedHoldingKind =
    String(ownership?.desktopClientHoldingKind || "") ||
    (mobilePreliminaryMode === "holdingActive"
      ? "active"
      : mobilePreliminaryMode === "holdingPassive"
      ? "passive"
      : "");
  const savedTrustNameKind = String(
    ownership?.accountHolderTrustResolution?.kind || ""
  );
  const savedMethod =
    String(ownership?.desktopClientCompletionMethod || "") ||
    (mobileEntryMode === "assistant"
      ? "guided"
      : mobileEntryMode === "manual"
      ? "manual"
      : "");
  const hasSavedOwnershipRows =
    (Array.isArray(ownership?.shareholders) && ownership.shareholders.length > 0) ||
    (Array.isArray(ownership?.ubos) && ownership.ubos.length > 0) ||
    (Array.isArray(ownership?.legalArrangements?.arrangements) &&
      ownership.legalArrangements.arrangements.length > 0);
  const hasSavedAssistantData =
    !!ownership?.assistantGenerated ||
    (Array.isArray(ownership?.assistantRoots) &&
      ownership.assistantRoots.length > 0) ||
    (Array.isArray(ownership?.mobileAssistantDraft?.roots) &&
      ownership.mobileAssistantDraft.roots.length > 0) ||
    !!ownership?.assistantTree;
  const guidedAlreadyCompleted =
    savedMethod === "guided" && (hasSavedAssistantData || hasSavedOwnershipRows);

  useEffect(() => {
    if (!uboModalOpen) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    const scrollbarW = window.innerWidth - html.clientWidth;
    if (scrollbarW > 0) body.style.paddingRight = `${scrollbarW}px`;
    html.style.overflow = "hidden";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
  }, [uboModalOpen]);

  useEffect(() => {
    if (visualBuilderLaunching) return;

    setProfile(initialProfile);
    setHoldingKind(savedHoldingKind);
    setTrustNameKind(savedTrustNameKind);
    setMethod(savedMethod);

    if (savedMethod === "manual") {
      setDetailsVisible(true);
      setTutorialVisible(false);
      setBuilderOpenKey("");
      setVisualBuilderCompleted(false);
      setVisualBuilderLaunching(false);
      return;
    }

    if (savedMethod === "visual") {
      const isCompleted = hasSavedAssistantData || hasSavedOwnershipRows;
      setDetailsVisible(isCompleted);
      setVisualBuilderCompleted(isCompleted);
      setTutorialVisible(!isCompleted);
      setVisualBuilderLaunching(false);
      return;
    }

    if (savedMethod === "guided") {
      setDetailsVisible(hasSavedAssistantData || hasSavedOwnershipRows);
      setVisualBuilderCompleted(false);
      setVisualBuilderLaunching(false);
      return;
    }

    if (!savedMethod && !hasSavedOwnershipRows && !hasSavedAssistantData) {
      setDetailsVisible(false);
      setVisualBuilderCompleted(false);
      setVisualBuilderLaunching(false);
    }
  }, [
    initialProfile,
    savedHoldingKind,
    savedTrustNameKind,
    savedMethod,
    hasSavedOwnershipRows,
    hasSavedAssistantData,
    visualBuilderLaunching,
  ]);

  const shareholdersKey = useMemo(() => JSON.stringify(
    (ownership?.shareholders || []).map((r) => ({
      rowId: r?._rowId,
      id: r?.treeNodeId,
      ...nameFields(r),
      name: r?.name,
      holderType: r?.holderType,
      shareholderEntityType: r?.shareholderEntityType,
      isPIC: r?.isPIC,
      isHoldingPassive: r?.isHoldingPassive,
      isHoldingActive: r?.isHoldingActive,
      isPicOrPassiveHolding: r?.isPicOrPassiveHolding,
      nat: r?.nationality,
      cor: r?.countryOfResidence,
      pct: r?.ownershipPercent,
    }))
  ), [ownership?.shareholders]); // eslint-disable-line
  const ubosKey = useMemo(() => JSON.stringify(
    (ownership?.ubos || []).map((r) => ({
      rowId: r?._rowId,
      id: r?.treeNodeId,
      subjectTreeNodeId: r?.subjectTreeNodeId,
      relatedShareholderRowId: r?.relatedShareholderRowId,
      relatedShareholderNodeId: r?.relatedShareholderNodeId,
      ...nameFields(r),
      name: r?.name,
      holderType: r?.holderType,
      nat: r?.nationality,
      cor: r?.countryOfResidence,
      pct: r?.effectiveOwnershipPercent || r?.ownershipPercent,
      basisOfControl: r?.basisOfControl,
    }))
  ), [ownership?.ubos]); // eslint-disable-line
  const legalArrangementsKey = useMemo(() => JSON.stringify(
    (ownership?.legalArrangements?.arrangements || []).map((r) => ({
      id: r?._rowId,
      ...nameFields(r),
      name: r?.name,
      type: r?.arrangementType,
      sourceRefRowId: r?.sourceRefRowId,
      sourceRefNodeId: r?.sourceRefNodeId,
      parties: (r?.parties || []).map((p) => ({
        id: p?._rowId,
        role: p?.role,
        ...nameFields(p),
        name: p?.name,
        trusteeKind: p?.trusteeKind,
        councilKind: p?.councilKind,
      })),
    }))
  ), [ownership?.legalArrangements]); // eslint-disable-line

  const liveOwnershipSnapshot = useMemo(
    () => buildLiveReviewOwnershipSnapshot({ ownership, accountHolderName: entityName }),
    [
      shareholdersKey,
      ubosKey,
      legalArrangementsKey,
      entityName,
      ownership?.assistantTree?.mermaid,
      ownership?.assistantTree?.initialAHType,
      ownership?.assistantTree?.nodes,
      ownership?.assistantTree?.flowLines,
      ownership?.assistantTree?.source,
      ownership?.desktopClientEntityType,
      ownership?.desktopClientHoldingKind,
    ] // eslint-disable-line
  );
  const liveOwnershipPercentWarning = useMemo(
    () => ownershipPercentWarning(liveOwnershipSnapshot),
    [liveOwnershipSnapshot]
  );
  const showVisualSummaryState = detailsVisible && !visualBuilderLaunching;

  const uboApiPayload = useMemo(
    () =>
      buildReviewOwnershipApiPayload({
        ownership,
        accountHolderName: entityName,
        liveOwnershipGraph: liveOwnershipSnapshot,
      }),
    [ownership, entityName, liveOwnershipSnapshot]
  );

  useEffect(() => {
    if (initialOwnershipDebugLoggedRef.current) return;

    const hasOwnershipData =
      !!liveOwnershipSnapshot?.mermaid ||
      Array.isArray(ownership?.shareholders) ||
      Array.isArray(ownership?.ubos) ||
      Array.isArray(ownership?.legalArrangements?.arrangements);

    if (!hasOwnershipData) return;

    initialOwnershipDebugLoggedRef.current = true;
    const copyDebugPayload = buildKycOwnershipDebugPayload({
      stage: "initial_load",
      entityName,
      ownership,
      liveOwnershipSnapshot,
      uboApiPayload,
    });

    logKycCopyDebugPayload(copyDebugPayload);
    logKycConsistencyDebug("initial_load", copyDebugPayload.consistency);

  }, [entityName, liveOwnershipSnapshot, ownership, uboApiPayload]);

  function handleOwnershipSetAtForDebug(path, value) {
    const isOwnershipUpdate = Array.isArray(path) && path[0] === "ownership";

    if (isOwnershipUpdate) {
      const nextOwnership = previewOwnershipAfterSetAt(ownership, path, value);
      const nextLiveGraph = buildLiveReviewOwnershipSnapshot({
        ownership: nextOwnership,
        accountHolderName: entityName,
      });
      const nextApiPayload = buildReviewOwnershipApiPayload({
        ownership: nextOwnership,
        accountHolderName: entityName,
        liveOwnershipGraph: nextLiveGraph,
      });

      const copyDebugPayload = buildKycOwnershipDebugPayload({
        stage: "manual_update",
        entityName,
        ownership: nextOwnership,
        liveOwnershipSnapshot: nextLiveGraph,
        uboApiPayload: nextApiPayload,
        updatePath: path,
        updatedValue: value,
      });

      logKycCopyDebugPayload(copyDebugPayload);
      logKycConsistencyDebug("manual_update", copyDebugPayload.consistency);

    }

    onSetAt?.(path, value);
  }

  const uboApiRows = useMemo(() => {
    const ids = Array.isArray(uboApiResult?.ubos) ? uboApiResult.ubos : [];
    return ids.map((id) => {
      const node = findNodeByApiId(uboApiPayload.nodes, id);
      return { id: String(id || ""), name: getNodeDisplayName(node, id), type: String(node?.type || "Individual") };
    });
  }, [uboApiPayload.nodes, uboApiResult]);

  const uboDirectShareholderRows = useMemo(() => {
    const ids = Array.isArray(uboApiResult?.directShareholders) ? uboApiResult.directShareholders : [];
    return ids.map((id) => {
      const node = findNodeByApiId(uboApiPayload.nodes, id);
      return { id: String(id || ""), name: getNodeDisplayName(node, id), type: String(node?.type || "Individual") };
    });
  }, [uboApiPayload.nodes, uboApiResult]);

  const uboLegalArrangementRows = useMemo(() => {
    const ids = Array.isArray(uboApiResult?.legalArrangements) ? uboApiResult.legalArrangements : [];
    return ids.map((id) => {
      const node = findNodeByApiId(uboApiPayload.nodes, id);
      const arrangement = findLegalArrangementForDetectedNode({
        ownership,
        detectedId: id,
        node,
      });
      return {
        id: String(id || ""),
        name: getNodeDisplayName(node, id),
        type: String(node?.type || "Legal arrangement"),
        parties: legalArrangementParties(arrangement),
      };
    });
  }, [ownership, uboApiPayload.nodes, uboApiResult]);

  const beneficialOwnerCount = uboDirectShareholderRows.length + uboApiRows.length;

  async function openUboIdentificationModal() {
    setUboModalOpen(true);
    setUboApiLoading(true);
    setUboApiError("");
    setUboApiResult(null);
    try {
      if (!uboApiPayload.initialAHType || !uboApiPayload.nodes.length) {
        throw new Error("No ownership tree is available for UBO identification.");
      }

      const copyDebugPayload = buildKycOwnershipDebugPayload({
        stage: "ubo_identification_click",
        entityName,
        ownership,
        liveOwnershipSnapshot,
        uboApiPayload,
      });
      const dataSentToApi = {
        initialAHType: uboApiPayload.initialAHType,
        nodes: uboApiPayload.nodes,
      };

      logKycCopyDebugPayload(copyDebugPayload);
      logKycConsistencyDebug("ubo_identification_click", copyDebugPayload.consistency);

      const result = await assessUbosFromTree(dataSentToApi.initialAHType, dataSentToApi.nodes, orgJurisdiction);

      setUboApiResult(result || {});
      const assessedOwnership = applyUboAssessmentResultToOwnership({
        ownership,
        apiPayload: uboApiPayload,
        apiResult: result || {},
        signature: "",
      });
      onSetAt?.(["ownership"], assessedOwnership);
    } catch (e) {
      setUboApiError(e?.message || "UBO identification failed.");
    } finally {
      setUboApiLoading(false);
    }
  }

  const profileReady =
    !!profile &&
    (profile !== "holding" || !!holdingKind) &&
    (profile !== "trust" || trustNameKind === "trust" || trustNameKind === "trustee");
  const missingProfileError = submitAttempted && !profile;
  const missingCompletionMethodError =
    submitAttempted && profileReady && !isTerminalAccountHolder && structureType === "complex" && !method;
  const missingStructureError =
    submitAttempted && profileReady && !isTerminalAccountHolder && !method && !structureType;
  // Type chosen at the start (kyc-sent, operating / holding / PIC): the structure is asked here, in the section,
  // not in a pop-up; and when the company documents showed a company among the shareholders, the structure is
  // multi-layer without asking.
  const inlineStructure = entityTypeAnsweredEarlier && UPLOAD_FIRST_ENTITY_TYPES.includes(profile);
  const askStructure = profileReady && !isTerminalAccountHolder && !method && !structureType && !readOnly;
  const documentsShowCompanyShareholder = form?.customer?.companyDocumentsReading?.corporateShareholders === true;
  // A completion method set outside this section (shareholders filled from the company documents) is followed.
  useEffect(() => {
    if (savedCompletionMethod && savedCompletionMethod !== method) {
      setMethod(savedCompletionMethod);
      setDetailsVisible(savedCompletionMethod === "manual");
    }
  }, [savedCompletionMethod]); // eslint-disable-line
  // Structure chosen in the section: the two cards fade out first, then the next step comes in as usual.
  const [leavingStructure, setLeavingStructure] = useState("");
  const leaveTimer = useRef(null);
  useEffect(() => () => clearTimeout(leaveTimer.current), []);
  // "3 Completion method" (multi-layer): in the main card, or in the structure card when the type was chosen at the start.
  function renderCompletionMethod() {
    return <>
              <div className="kycClientOwnStepHead">
                <span>3</span>
                <div>
                  <strong>Completion method</strong>
                  <p>{showManualEntryMethod
                    ? "Use the visual builder or enter the ownership details manually."
                    : "Visual builder is recommended, with guided chat assistant available."}</p>
                </div>
              </div>

              <div className="kycClientOwnMethodGrid">
                {METHOD_OPTIONS.filter((option) => {
                  if (option.value === "guided" && !showGuidedMethod) return false;
                  if (option.value === "manual" && !showManualEntryMethod) return false;
                  return true;
                }).map((option) => {
                  const guidedGenerated =
                    option.value === "guided" &&
                    method === "guided" &&
                    detailsVisible;
                  const visualGenerated =
                    option.value === "visual" &&
                    method === "visual" &&
                    visualBuilderCompleted;
                  return (
                    <SelectionCard
                      key={option.value}
                      option={option}
                      active={
                        option.value === "guided"
                          ? guidedGenerated
                          : option.value === "visual"
                          ? visualGenerated
                          : method === option.value
                      }
                      onClick={() => chooseMethod(option.value)}
                      disabled={guidedGenerated || visualGenerated}
                      className={option.value === "guided" ? "is-recommended" : ""}
                      forceThreeLineModel={methodCardsNeedThreeLineModel}
                      onHintLineCountChange={(lineCount) =>
                        handleMethodHintLineCount(option.value, lineCount)
                      }
                    />
                  );
                })}
              </div>
              {missingCompletionMethodError ? (
                <InlineDangerMessage centered>
                  Please select a completion method.
                </InlineDangerMessage>
              ) : null}
    </>;
  }

  function chooseStructureInline(nextStructure) {
    if (leavingStructure) return;
    setLeavingStructure(nextStructure);
    leaveTimer.current = setTimeout(() => {
      setLeavingStructure("");
      chooseStructure(nextStructure);
    }, STRUCTURE_FADE_MS);
  }
  useEffect(() => {
    if (inlineStructure && documentsShowCompanyShareholder && askStructure) chooseStructure("complex");
  }, [inlineStructure, documentsShowCompanyShareholder, askStructure]); // eslint-disable-line

  function applyProfile(nextProfile, nextHoldingKind = holdingKind, nextTrustKind = trustNameKind) {
    const nextFlags = {
      ...flags,
      pctNotMeaningful:
        nextProfile === "pic" ||
        (nextProfile === "holding" && nextHoldingKind === "passive"),
      quotedCompany: nextProfile === "quoted",
      accountHolderTrust: nextProfile === "trust",
      accountHolderFoundation: nextProfile === "foundation",
    };

    onSetAt?.(["ownership", "uboFlags"], nextFlags);
    onSetAt?.(["ownership", "desktopClientEntityType"], nextProfile || "");
    onSetAt?.(["ownership", "desktopClientHoldingKind"], nextProfile === "holding" ? nextHoldingKind || "" : "");
    onSetAt?.(
      ["ownership", "preliminaryNoneOfAbove"],
      nextProfile === "operating" ||
        (nextProfile === "holding" && nextHoldingKind === "active")
    );

    if (nextProfile === "trust" && nextTrustKind) {
      onSetAt?.(["ownership", "accountHolderTrustResolution"], {
        kind: nextTrustKind,
        sourceName: String(form?.customer?.name || "").trim(),
      });
    } else if (nextProfile !== "trust") {
      onSetAt?.(["ownership", "accountHolderTrustResolution"], null);
    }
  }

  function clearTypeDependentOwnershipData() {
    onSetAt?.(["ownership", "shareholders"], []);
    onSetAt?.(["ownership", "directShareholders"], []);
    onSetAt?.(["ownership", "ubos"], []);
    onSetAt?.(["ownership", "controllingPersonsNatural"], []);
    onSetAt?.(["ownership", "legalArrangements"], { arrangements: [] });
    onSetAt?.(["ownership", "controllingPersonsLegalArrangements"], {
      arrangements: [],
    });
    onSetAt?.(["ownership", "assistantTree"], null);
    onSetAt?.(["ownership", "assistantRoots"], []);
    onSetAt?.(["ownership", "desktopAssistantMode"], "");
    onSetAt?.(["ownership", "assistantGenerated"], false);
    onSetAt?.(["ownership", "mobileAssistantDraft"], null);
    onSetAt?.(["ownership", "uboAssessment"], null);
    onSetAt?.(["ownership", "qualifiedUbos"], []);
    setPercentsNeeded(false);
  }

  // Note after a type change that now requires percentages, until all are entered.
  const clientCarriesPercentNow = profile === "operating" || (profile === "holding" && holdingKind === "active");
  const shareholdersNote = percentsNeeded && clientCarriesPercentNow &&
    (Array.isArray(ownership?.shareholders) ? ownership.shareholders : [])
      .some((row) => String(row?.name || "").trim() && !String(row?.ownershipPercent ?? "").trim())
    ? "Please enter each shareholder's percentage."
    : "";

  const hasOwnershipDetails =
    [ownership?.shareholders, ownership?.ubos, ownership?.legalArrangements?.arrangements]
      .some((list) => (Array.isArray(list) ? list : []).some((row) => String(row?.name || "").trim())) ||
    (Array.isArray(ownership?.assistantTree?.nodes) && ownership.assistantTree.nodes.length > 1);

  function writeSignatories(signatories) {
    const current = Array.isArray(ownership?.signatories) ? ownership.signatories : [];
    if (JSON.stringify(current) !== JSON.stringify(signatories || [])) {
      onSetAt?.(["ownership", "signatories"], signatories || []);
    }
  }

  // Removes every ownership party and what is attached to them (documents deleted; linked
  // signatories asked one by one), then continues.
  function removeAllOwnershipThen(next, { title, intro, applyLabel }) {
    requestRemoval(allOwnershipParties(ownership), {
      mode: "delete",
      title: clientFacing ? "Clear all ownership details?" : title,
      intro: clientFacing ? "Everything you've entered in this section will be cleared." : intro,
      applyLabel: clientFacing ? "Clear" : applyLabel,
      backLabel: "Cancel",
      ...(clientFacing ? CLIENT_REMOVAL_LABELS(entityName) : {}),
      onConfirmed: ({ signatories }) => {
        writeSignatories(signatories);
        next();
      },
    });
  }

  // Client-type card: same card does nothing; with ownership details entered, a change between
  // operating company / holding / PIC can keep the structure, any other change removes it.
  function selectProfile(nextProfile) {
    if (readOnly || nextProfile === profile) return;
    setPendingHoldingChoice(false);
    if (!profile || !hasOwnershipDetails) {
      applySelectedProfile(nextProfile);
      return;
    }
    // Holding from an operating company or PIC: active/passive is chosen first, then the type
    // changes with the structure kept (see selectHoldingKind).
    if (nextProfile === "holding" && ["operating", "pic"].includes(profile)) {
      setPendingHoldingChoice(true);
      return;
    }
    // Between operating company / holding / PIC no party is removed: the structure is kept, no
    // question. Changes that remove parties (trust, foundation, quoted) ask first.
    if (["operating", "holding", "pic"].includes(profile) && ["operating", "pic"].includes(nextProfile)) {
      keepStructureWithProfile(nextProfile);
      return;
    }
    setTypeChange({ next: nextProfile });
  }

  // Only the client's type changes; rows and structure stay (UBOs are re-checked automatically).
  function keepStructureWithProfile(nextProfile, nextHoldingKind = "") {
    const hadPercent = profile === "operating" || (profile === "holding" && holdingKind === "active");
    const needsPercent = nextProfile === "operating" || (nextProfile === "holding" && nextHoldingKind === "active");
    if (needsPercent && !hadPercent) setPercentsNeeded(true);
    if (!needsPercent) setPercentsNeeded(false);
    setKeepRowsToken((t) => t + 1);
    setProfile(nextProfile);
    setHoldingKind(nextProfile === "holding" ? nextHoldingKind : "");
    setTrustNameKind("");
    applyProfile(nextProfile, nextProfile === "holding" ? nextHoldingKind : "", "");
  }

  // Start over: the type (and holding kind) is applied after everything has been removed.
  function startOverWithProfile(nextProfile, nextHoldingKind = "") {
    applySelectedProfile(nextProfile);
    if (nextProfile === "holding" && nextHoldingKind) {
      setHoldingKind(nextHoldingKind);
      applyProfile("holding", nextHoldingKind, "");
    }
  }

  function applySelectedProfile(nextProfile) {
    if (nextProfile !== profile) {
      clearTypeDependentOwnershipData();
    }

    setProfile(nextProfile);
    onSetAt?.(["ownership", "desktopClientStructureType"], "");
    setMethod("");
    onSetAt?.(["ownership", "desktopClientCompletionMethod"], "");
    setDetailsVisible(false);
    setVisualBuilderCompleted(false);
    setVisualBuilderLaunching(false);

    const nextHolding = nextProfile === "holding" ? holdingKind : "";
    const nextTrust = nextProfile === "trust" ? trustNameKind : "";
    if (nextProfile !== "holding") setHoldingKind("");
    if (nextProfile !== "trust") setTrustNameKind("");
    applyProfile(nextProfile, nextHolding, nextTrust);
  }

  function selectHoldingKind(nextKind) {
    if (readOnly) return;
    // Holding chosen from an operating company / PIC with ownership details: now ask.
    if (pendingHoldingChoice) {
      setPendingHoldingChoice(false);
      keepStructureWithProfile("holding", nextKind);
      return;
    }
    // Active ↔ passive with ownership details: the structure is kept.
    if (profile === "holding" && holdingKind && nextKind !== holdingKind && hasOwnershipDetails) {
      keepStructureWithProfile("holding", nextKind);
      return;
    }
    if (nextKind !== holdingKind) {
      onSetAt?.(["ownership", "desktopClientStructureType"], "");
      onSetAt?.(["ownership", "desktopClientCompletionMethod"], "");
      setMethod("");
      setDetailsVisible(false);
      setVisualBuilderCompleted(false);
      setVisualBuilderLaunching(false);
    }
    setHoldingKind(nextKind);
    applyProfile("holding", nextKind, trustNameKind);
  }

  function selectTrustNameKind(nextKind) {
    setTrustNameKind(nextKind);
    applyProfile("trust", holdingKind, nextKind);
  }

  function chooseStructure(nextStructure) {
    onSetAt?.(["ownership", "desktopClientStructureType"], nextStructure);
    if (nextStructure === "direct") {
      chooseMethod("manual");
      const initialShareholders = [{ ...emptyShareholderRow(), holderType: "Individual" }];
      onSetAt?.(["ownership", "shareholders"], initialShareholders);
      onSetAt?.(["ownership", "directShareholders"], initialShareholders);
    }
  }

  function chooseMethod(nextMethod) {
    if (nextMethod !== method) {
      clearTypeDependentOwnershipData();
      setVisualBuilderCompleted(false);
      setVisualBuilderLaunching(false);
    }
    setMethod(nextMethod);
    onSetAt?.(["ownership", "desktopClientCompletionMethod"], nextMethod || "");
    setDetailsVisible(nextMethod === "manual");
    if (nextMethod !== "visual") {
      setTutorialVisible(false);
      setBuilderOpenKey("");
    }
    if (nextMethod === "visual") {
      if (skipTutorial) openVisualBuilder();
      else setTutorialVisible(true);
    }
  }

  // Reset: everything is removed, with the documents of the ownership parties; the only question
  // asked is for signatories linked to them.
  function resetOwnershipIntro() {
    if (readOnly) return;
    if (!hasOwnershipDetails) {
      doResetOwnershipIntro();
      return;
    }
    removeAllOwnershipThen(doResetOwnershipIntro, {
      title: "Reset ownership information?",
      intro: "All ownership details will be removed, with the documents of these parties:",
      applyLabel: "Reset",
    });
  }

  function doResetOwnershipIntro() {
    clearTypeDependentOwnershipData();
    onSetAt?.(["ownership", "desktopClientStructureType"], "");
    if (!entityTypeAnsweredEarlier) {
      setProfile("");
      setHoldingKind("");
      setTrustNameKind("");
    }
    setMethod("");
    setDetailsVisible(false);
    setTutorialVisible(!skipTutorial);
    setBuilderOpenKey("");
    setVisualBuilderCompleted(false);
    setVisualBuilderLaunching(false);
    onSetAt?.(["ownership", "desktopClientCompletionMethod"], "");
    // The type answered in the upload-first step stays.
    if (entityTypeAnsweredEarlier) applyProfile(profile, holdingKind, trustNameKind);
    else applyProfile("", "", "");
  }

  function resetVisualBuilderIncomplete() {
    if (visualBuilderCompleted) {
      setDetailsVisible(true);
      setTutorialVisible(false);
      setBuilderOpenKey("");
      setVisualBuilderLaunching(false);
      return;
    }

    setMethod("");
    onSetAt?.(["ownership", "desktopClientCompletionMethod"], "");
    setDetailsVisible(false);
    setTutorialVisible(true);
    setBuilderOpenKey("");
    setVisualBuilderCompleted(false);
    setVisualBuilderLaunching(false);
  }

  function closeVisualBuilderTutorial() {
    setTutorialVisible(false);
    if (visualBuilderCompleted || detailsVisible) {
      return;
    }

    setMethod("");
    onSetAt?.(["ownership", "desktopClientCompletionMethod"], "");
  }

  function showVisualBuilderTutorial() {
    if (!method) {
      setMethod("visual");
      onSetAt?.(["ownership", "desktopClientCompletionMethod"], "visual");
    }
    setTutorialVisible(true);
  }

  function openVisualBuilder() {
    setTutorialVisible(false);
    setVisualBuilderLaunching(true);
    setDetailsVisible(true);
    setBuilderOpenKey(`visual_${Date.now()}`);
  }

  const entityCardsNeedThreeLineModel = Object.values(entityHintLineCounts).some(
    (lineCount) => Number(lineCount) >= 3
  );
  const methodCardsNeedThreeLineModel = Object.values(methodHintLineCounts).some(
    (lineCount) => Number(lineCount) >= 3
  );

  function handleEntityHintLineCount(optionValue, lineCount) {
    setEntityHintLineCounts((prev) => {
      if (prev[optionValue] === lineCount) return prev;
      return { ...prev, [optionValue]: lineCount };
    });
  }

  function handleMethodHintLineCount(optionValue, lineCount) {
    setMethodHintLineCounts((prev) => {
      if (prev[optionValue] === lineCount) return prev;
      return { ...prev, [optionValue]: lineCount };
    });
  }

  function applyGuidedOwnership(payload) {
    onSetAt?.(["ownership", "shareholders"], payload?.shareholders || []);
    onSetAt?.(["ownership", "directShareholders"], payload?.shareholders || []);
    onSetAt?.(["ownership", "ubos"], payload?.ubos || []);
    onSetAt?.(["ownership", "controllingPersonsNatural"], payload?.ubos || []);
    onSetAt?.(["ownership", "legalArrangements"], {
      arrangements: payload?.arrangements || [],
    });
    onSetAt?.(["ownership", "controllingPersonsLegalArrangements"], {
      arrangements: payload?.arrangements || [],
    });
    onSetAt?.(["ownership", "assistantTree"], payload?.assistantTree || null);
    onSetAt?.(["ownership", "assistantRoots"], payload?.assistantRoots || []);
    onSetAt?.(["ownership", "desktopAssistantMode"], payload?.desktopAssistantMode || "");
    onSetAt?.(["ownership", "assistantGenerated"], true);
    setDetailsVisible(true);
    setVisualBuilderLaunching(false);
    scheduleBodyScrollUnlockCleanup();
  }

  const typeChangeLabel = typeChange?.next === "holding" && typeChange?.holdingKind
    ? `${typeChange.holdingKind === "active" ? "an active" : "a passive"} holding`
    : {
      operating: "an operating company", holding: "a holding company", pic: "a domiciliary company (PIC)",
      trust: "a trust", foundation: "a foundation", quoted: "a quoted company",
    }[typeChange?.next] || "this type";
  const typeChangeCanKeep = typeChange &&
    ["operating", "holding", "pic"].includes(profile) && ["operating", "holding", "pic"].includes(typeChange.next);
  // From a type without percentages (PIC, passive holding) to one that requires them.
  const typeChangeNeedsPercent = !!typeChange &&
    (profile === "pic" || (profile === "holding" && holdingKind === "passive")) &&
    (typeChange.next === "operating" || (typeChange.next === "holding" && typeChange.holdingKind === "active"));
  const cancelTypeChange = () => {
    setTypeChange(null);
    setPendingHoldingChoice(false);
  };
  const startOverForTypeChange = () => {
    const next = typeChange?.next;
    const nextHoldingKind = typeChange?.holdingKind || "";
    setTypeChange(null);
    setPendingHoldingChoice(false);
    removeAllOwnershipThen(() => startOverWithProfile(next, nextHoldingKind), {
      title: "Remove the ownership details?",
      intro: "The ownership details will be removed, with the documents of these parties:",
      applyLabel: "Remove and change",
    });
  };

  return (
    <div className="kycClientOwnDesktop">
      {removalDialog}
      <OwnershipChoiceDialog
        open={!!typeChange}
        title={clientFacing ? `Change ${entityName} to ${ownershipLabel(typeChangeLabel)}?` : `Change the client type to ${ownershipLabel(typeChangeLabel)}?`}
        hint={typeChangeCanKeep
          ? (clientFacing ? "What should happen to the owners you've already entered?" : "Choose what happens to the ownership details already entered.")
          : ""}
        message={typeChangeCanKeep ? "" : clientFacing
          ? `The owners and the structure you've entered will be cleared, because ${ownershipLabel(typeChangeLabel)} is described differently.`
          : "The ownership details entered so far (shareholders, controlling persons, trusts and the diagram) will be removed."}
        options={typeChangeCanKeep ? [
          {
            value: "keep",
            label: clientFacing ? "Keep my answers" : "Keep the ownership structure",
            hint: clientFacing
              ? "Only the type of company changes. The owners you've entered stay."
              : "Only the client type changes. Shareholders and the structure below stay; UBOs are identified again with the new rules.",
            note: typeChangeNeedsPercent
              ? (clientFacing ? "You'll need to enter each shareholder's percentage." : "Shareholder percentages are required for this client type and will need to be entered.")
              : "",
            confirmLabel: "Change type",
          },
          {
            value: "start",
            label: clientFacing ? "Start again" : "Start over",
            hint: clientFacing ? "Clear the owners you've entered." : "Remove the current ownership details.",
            confirmLabel: clientFacing ? "Clear and change" : "Remove and change",
            danger: true,
          },
        ] : []}
        confirmLabel={clientFacing ? "Clear and change" : "Remove and change"}
        danger
        onCancel={cancelTypeChange}
        onConfirm={(choice) => {
          if (choice === "keep") {
            const { next, holdingKind: nextHoldingKind = "" } = typeChange;
            setTypeChange(null);
            setPendingHoldingChoice(false);
            keepStructureWithProfile(next, nextHoldingKind);
          } else {
            startOverForTypeChange();
          }
        }}
      />
      <div className="card kycCard mb-3 kycClientOwnIntroCard">
        <div className="kycSectionHead">
          <h5 className="kycSectionTitle mb-0">
            {sectionNo} | Ownership & Control
          </h5>
          {showGuidedMethod && (
            <div className="kycSectionHint">
              First tell us what kind of account holder {entityName} is, then
              choose how you would like to complete the ownership details
            </div>
          )}
        </div>

        <div className="kycSectionBody">
          <div className="kycClientOwnStepHead kycClientOwnStepHead--withAction">
            <div className="kycClientOwnStepHeadMain">
              <span>1</span>
              <div>
                <strong>Client entity type</strong>
                <p>Choose the option that best describes {entityName}.</p>
              </div>
            </div>
            <button
              type="button"
              className="kycSigAddBtn"
              onClick={resetOwnershipIntro}
              disabled={readOnly}
            >
              Reset Ownership Information
            </button>
          </div>

          {/* Type chosen at the start: the other types are muted (still selectable). */}
          <div className={`kycClientOwnGrid${entityTypeAnsweredEarlier && profile ? " is-muted-others" : ""}`}>
            {ACCOUNT_HOLDER_OPTIONS.map((option) => (
              <SelectionCard
                key={option.value}
                option={{ ...option, title: ownershipLabel(option.title) }}
                active={pendingHoldingChoice ? option.value === "holding" : profile === option.value}
                onClick={() => selectProfile(option.value)}
                forceThreeLineModel={entityCardsNeedThreeLineModel}
                onHintLineCountChange={(lineCount) =>
                  handleEntityHintLineCount(option.value, lineCount)
                }
              />
            ))}
          </div>
          {missingProfileError ? (
            <InlineDangerMessage centered>
              Please select the client entity type.
            </InlineDangerMessage>
          ) : null}

          {profile === "holding" || pendingHoldingChoice ? (
            <div className="kycClientOwnHoldingType kycClientOwnReveal">
              <div className="kycClientOwnHoldingTypeHead">
                <strong>Holding type</strong>
                <p>Specify whether the holding company is active or passive.</p>
              </div>
              <div className="kycClientOwnHoldingTypeGrid">
                {HOLDING_TYPE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={holdingKind === option.value ? "is-active" : ""}
                    onClick={() => selectHoldingKind(option.value)}
                  >
                    <strong>{option.label}</strong>
                    <span>{option.hint}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {profile === "trust" ? (
            <div className="kycClientOwnHoldingType kycClientOwnReveal">
              <div className="kycClientOwnHoldingTypeHead">
                <strong>Trust name</strong>
                <p>
                  Specify whether the client name refers to the trust or to the
                  trustee.
                </p>
              </div>
              <div className="kycClientOwnHoldingTypeGrid">
                {TRUST_NAME_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={trustNameKind === option.value ? "is-active" : ""}
                    onClick={() => selectTrustNameKind(option.value)}
                  >
                    <strong>{option.label}</strong>
                    <span>{option.hint}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {profileReady && profile === "quoted" ? (
            <div className="kycClientOwnFinalState">
              <span className="kycClientOwnFinalIcon">
                <i className="bi bi-check2-circle" aria-hidden="true" />
              </span>
              <div>
                <strong>No further ownership details required</strong>
                <p>
                  Direct shareholders and controlling persons are not required
                  because {entityName} is a quoted company.
                </p>
              </div>
            </div>
          ) : null}

          {profileReady && (profile === "trust" || profile === "foundation") ? (
            <>
              <div className="kycClientOwnDivider" />
              <div className="kycClientOwnStepHead kycClientOwnStepHead--withAction">
                <div className="kycClientOwnStepHeadMain">
                  <span>2</span>
                  <div>
                    <strong>
                      {profile === "foundation"
                        ? "Foundation parties"
                        : "Trust parties"}
                    </strong>
                    <p>
                      Complete the parties connected to the{" "}
                      {profile === "foundation" ? "foundation" : "trust"}.
                    </p>
                  </div>
                </div>
                {forceApiCallInTree ? (
                  <button
                    type="button"
                    className="kycClientOwnTextBtn kycClientOwnStepActionBtn"
                    onClick={openUboIdentificationModal}
                    disabled={uboApiLoading}
                  >
                    <i className="bi bi-person-check me-1" aria-hidden="true" />
                    {uboApiLoading ? "Identifying..." : "UBOs Identification"}
                  </button>
                ) : null}
              </div>

              <KycSectionOwnership
                separateNames={separateNames}
                orgJurisdiction={orgJurisdiction}
                sectionNo={sectionNo}
                form={form}
                mode={mode}
                forceApiCallInTree={forceApiCallInTree}
                onSetAt={handleOwnershipSetAtForDebug}
                changedClass={changedClass}
                readOnly={readOnly}
                hideOwnershipAssistantIntro
                hidePreliminaryInformation
                showSignatories={false}
                assistantInitialAccountHolderType={selectedVisualType}
                assistantInitialAccountHolderTrustNameKind={trustNameKind}
                submitAttempted={submitAttempted}
                assistantSnapshotOverride={liveOwnershipSnapshot}
                keepRowsOnModeChangeToken={keepRowsToken}
                clientFacing={clientFacing}
                shareholdersNote={shareholdersNote}
                assistantSnapshotWarning={liveOwnershipPercentWarning}
                hideAssistantSnapshot
                hideControllingPersonsDivider
                hideControllingPersonsHeadings
                embedded
              />
            </>
          ) : null}

          {!inlineStructure && entityTypeAnsweredEarlier && structureModalDismissed && askStructure ? (
            <div className="kycClientOwnResetRow">
              <button type="button" className="kycSigAddBtn" onClick={() => setStructureModalDismissed(false)}>
                Choose the ownership structure
              </button>
            </div>
          ) : null}
          <OwnershipStructureChoiceModal
            open={!inlineStructure && askStructure &&
              !structurePromptPaused && !(entityTypeAnsweredEarlier && structureModalDismissed)}
            // The type is not asked here when answered earlier: closing keeps it.
            onClose={() => (entityTypeAnsweredEarlier ? setStructureModalDismissed(true) : selectProfile(""))}
            value={structureType}
            onChange={chooseStructure}
            disabled={readOnly}
          />
          {missingStructureError ? (
            <InlineDangerMessage centered>Please select an ownership structure.</InlineDangerMessage>
          ) : null}

          <AnimatePresence initial={false}>
            {!inlineStructure && profileReady && !isTerminalAccountHolder && structureType === "complex" && !method ? (
              <motion.div
                key="completion-method"
                {...ownershipSectionMotion}
                style={{ overflow: "hidden" }}
              >
              <div className="kycClientOwnDivider" />
              {renderCompletionMethod()}

              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>

      {/* Type chosen at the start: the structure question in its own card (as the other steps' cards). Multi-layer:
          the choice fades out and the completion method takes its place in the same card; direct: the card goes. */}
      <AnimatePresence initial={false}>
        {inlineStructure && profileReady && !isTerminalAccountHolder && !readOnly && !method &&
          (!structureType || structureType === "complex") ? (
          <motion.div key="structure-card" {...ownershipSectionMotion} style={{ overflow: "hidden" }}>
            <div className="card kycCard mb-3 kycClientOwnStructureCard">
              {/* Multi-layer: 1. the choice fades out, 2. the card resizes to the completion method, 3. it fades in. */}
              <MeasuredHeight>
              <AnimatePresence mode="wait" initial={false}>
                {!structureType ? (
                  <motion.div key="structure" initial={{ opacity: 0 }} animate={{ opacity: leavingStructure ? 0 : 1 }}
                    exit={{ opacity: 0, transition: { duration: 0 } }}
                    transition={{ duration: STRUCTURE_FADE_MS / 1000, ease: "easeOut" }}>
                    <div className="kycClientOwnStepHead">
                      <span>2</span>
                      <div>
                        <strong>Ownership structure</strong>
                        <p>Choose the structure that best describes {entityName}'s ownership.</p>
                      </div>
                    </div>
                    <OwnershipStructureChoice value={leavingStructure || structureType} onChange={chooseStructureInline} disabled={readOnly} />
                  </motion.div>
                ) : (
                  <motion.div key="completion-method" initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { delay: STRUCTURE_RESIZE_MS / 1000, duration: STRUCTURE_FADE_MS / 1000, ease: "easeOut" } }}
                    exit={{ opacity: 0 }}>
                    {renderCompletionMethod()}
                  </motion.div>
                )}
              </AnimatePresence>
              </MeasuredHeight>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {method === "manual" && profileReady && !isTerminalAccountHolder ? (
          <motion.div
            key="manual-entry-card"
            {...ownershipSectionMotion}
            style={{ overflow: "hidden" }}
          >
        <div className="card kycCard mb-3 kycClientOwnActionCard kycClientOwnActionCard--summary">
          <div className="kycClientOwnActionCardMain">
            <div className="kycClientOwnGeneratedHeaderText">
              <div className="kycClientOwnEyebrow">Manual Entry</div>
              <strong>Ownership details</strong>
              <p>
                Complete the direct shareholders and controlling persons below.
              </p>
            </div>
            {forceApiCallInTree ? (
              <div className="kycClientOwnActionButtons">
                <button
                  type="button"
                  className="kycClientOwnTextBtn"
                  onClick={openUboIdentificationModal}
                  disabled={uboApiLoading}
                >
                  <i className="bi bi-person-check me-1" aria-hidden="true" />
                  {uboApiLoading ? "Identifying..." : "UBOs Identification"}
                </button>
              </div>
            ) : null}
          </div>

          <div className="kycClientOwnGeneratedSummary">
            <KycSectionOwnership
              separateNames={separateNames}
              orgJurisdiction={orgJurisdiction}
              sectionNo={sectionNo}
              form={form}
              mode={mode}
              forceApiCallInTree={forceApiCallInTree}
              onSetAt={handleOwnershipSetAtForDebug}
              changedClass={changedClass}
              readOnly={readOnly}
              hideOwnershipAssistantIntro
              hidePreliminaryInformation
              disableAutoEmptyShareholder
              showSignatories={false}
              assistantInitialAccountHolderType={selectedVisualType}
	              assistantInitialAccountHolderTrustNameKind={trustNameKind}
	              assistantSnapshotOverride={liveOwnershipSnapshot}
                keepRowsOnModeChangeToken={keepRowsToken}
                clientFacing={clientFacing}
                shareholdersNote={shareholdersNote}
	              assistantSnapshotWarning={liveOwnershipPercentWarning}
	              embedded
	            />
          </div>
        </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {profileReady &&
      !isTerminalAccountHolder &&
      tutorialVisible &&
      method === "visual"
        ? createPortal(
            <VisualBuilderTutorial
              onStart={openVisualBuilder}
              onClose={closeVisualBuilderTutorial}
              builderAlreadyOpen={skipTutorial}
            />,
            document.body
          )
        : null}

      <AnimatePresence initial={false}>
        {method === "visual" && profileReady && !isTerminalAccountHolder && !tutorialVisible ? (
          <motion.div
            key="visual-builder-card"
            {...ownershipVisualCardMotion}
            style={{ transformOrigin: "top center", willChange: "opacity, transform" }}
          >
          <div
            className={`card kycCard mb-3 kycClientOwnActionCard ${
              showVisualSummaryState ? "kycClientOwnActionCard--summary" : ""
            }`}
          >
            <div className="kycClientOwnActionCardMain">
              <div className={showVisualSummaryState ? "kycClientOwnGeneratedHeaderText" : ""}>
                <div className="kycClientOwnEyebrow">
                  {showVisualSummaryState ? "Visual Ownership Builder" : "Visual builder"}
                </div>
                <strong>
                  {showVisualSummaryState
                    ? "Ownership summary generated"
                    : "Build or update the ownership chart"}
                </strong>
                <p>
                  {showVisualSummaryState
                    ? "Review and edit the generated ownership details below :"
                    : "The client entity type is already selected. Open the builder to drag shareholders into the chart."}
                </p>
              </div>
              <div className="kycClientOwnActionButtons">
                {forceApiCallInTree ? (
                  <button
                    type="button"
                    className="kycClientOwnTextBtn"
                    onClick={openUboIdentificationModal}
                    disabled={uboApiLoading}
                  >
                    <i className="bi bi-person-check me-1" aria-hidden="true" />
                    {uboApiLoading ? "Identifying..." : "UBOs Identification"}
                  </button>
                ) : null}
                {!visualBuilderCompleted ? (
                  <button type="button" className="kycClientOwnPrimaryBtn" onClick={openVisualBuilder}>
                    Open visual builder
                  </button>
                ) : null}
              </div>
            </div>

            {detailsVisible ? (
              <div
                className={`kycClientOwnGeneratedSummary${
                  visualBuilderLaunching ? " is-launching-builder" : ""
                }`}
              >
                <KycSectionOwnership
                  separateNames={separateNames}
                  orgJurisdiction={orgJurisdiction}
                  key={builderOpenKey || "visual-builder-summary"}
                  sectionNo={sectionNo}
                  form={form}
                  mode={mode}
                forceApiCallInTree={forceApiCallInTree}
                  onSetAt={handleOwnershipSetAtForDebug}
                  changedClass={changedClass}
                  readOnly={readOnly}
                  hideOwnershipAssistantIntro
                  hidePreliminaryInformation
                  showSignatories={false}
                  assistantInitialAccountHolderType={selectedVisualType}
                  assistantInitialAccountHolderTrustNameKind={trustNameKind}
                  autoOpenAssistantKey={builderOpenKey}
                  onAssistantOpenChange={onVisualBuilderModalOpenChange}
                  onAssistantApplied={() => {
                    setVisualBuilderCompleted(true);
                    setDetailsVisible(true);
                    setVisualBuilderLaunching(false);
                    setBuilderOpenKey("");
                  }}
                  onAssistantCloseIncomplete={() => {
                    setVisualBuilderLaunching(false);
                    resetVisualBuilderIncomplete();
                  }}
	                  submitAttempted={submitAttempted}
	                  assistantSnapshotOverride={liveOwnershipSnapshot}
                keepRowsOnModeChangeToken={keepRowsToken}
                clientFacing={clientFacing}
                shareholdersNote={shareholdersNote}
	                  assistantSnapshotWarning={liveOwnershipPercentWarning}
	                  onHowItWorks={skipTutorial ? showVisualBuilderTutorial : undefined}
	                  embedded
                />
              </div>
            ) : null}
          </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {method === "guided" && profileReady && !isTerminalAccountHolder ? (
        <DesktopGuidedOwnershipAssistant
          separateNames={separateNames}
          profile={profile}
          holdingKind={holdingKind}
          entityName={entityName}
          onGenerateOwnership={applyGuidedOwnership}
          initialSubmitted={detailsVisible || guidedAlreadyCompleted}
          documentShareholders={ownershipHasShareholders ? null : ownership.documentShareholders}

          onExitIncomplete={() => {
            setMethod("");
            setDetailsVisible(false);
          }}
          summaryContent={
            detailsVisible ? (
              <KycSectionOwnership
                separateNames={separateNames}
                orgJurisdiction={orgJurisdiction}
                sectionNo={sectionNo}
                form={form}
                mode={mode}
                forceApiCallInTree={forceApiCallInTree}
                onSetAt={handleOwnershipSetAtForDebug}
                changedClass={changedClass}
                readOnly={readOnly}
                hideOwnershipAssistantIntro
                hidePreliminaryInformation
                showSignatories={false}
                assistantInitialAccountHolderType={selectedVisualType}
                assistantInitialAccountHolderTrustNameKind={trustNameKind}
	                submitAttempted={submitAttempted}
	                assistantSnapshotOverride={liveOwnershipSnapshot}
                keepRowsOnModeChangeToken={keepRowsToken}
                clientFacing={clientFacing}
                shareholdersNote={shareholdersNote}
	                assistantSnapshotWarning={liveOwnershipPercentWarning}
	                embedded
	              />
            ) : null
          }
        />
      ) : null}

      {profileReady &&
      detailsVisible &&
      method !== "manual" &&
      method !== "guided" &&
      method !== "visual" &&
      !isTerminalAccountHolder ? (
        <KycSectionOwnership
          separateNames={separateNames}
          orgJurisdiction={orgJurisdiction}
          sectionNo={sectionNo}
          form={form}
          mode={mode}
          forceApiCallInTree={forceApiCallInTree}
          onSetAt={handleOwnershipSetAtForDebug}
          changedClass={changedClass}
          readOnly={readOnly}
          hideOwnershipAssistantIntro
          hidePreliminaryInformation
          showSignatories={false}
          assistantInitialAccountHolderType={selectedVisualType}
          assistantInitialAccountHolderTrustNameKind={trustNameKind}
          autoOpenAssistantKey={method === "visual" ? builderOpenKey : ""}
          copyOverrides={{
            header: {
              kicker: null,
              title: " | Ownership details",
              hint:
                method === "visual"
                  ? "Review the details generated by the visual builder, or edit them below."
                  : "Complete or adjust the ownership details below.",
            },
          }}
	          submitAttempted={submitAttempted}
	          assistantSnapshotOverride={liveOwnershipSnapshot}
                keepRowsOnModeChangeToken={keepRowsToken}
                clientFacing={clientFacing}
                shareholdersNote={shareholdersNote}
	          assistantSnapshotWarning={liveOwnershipPercentWarning}
	        />
      ) : null}

      <div
          className={`kycReviewClientTypeModalBackdrop${uboModalOpen ? " isOpen" : ""}`}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setUboModalOpen(false);
          }}
        >
          <motion.div
            className="kycReviewUboModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="kycOwnUboModalTitle"
            layout
            transition={{ layout: { duration: 0.22, ease: "easeOut" } }}
          >
            <div className="kycReviewClientTypeModalHead">
              <div>
                <div id="kycOwnUboModalTitle" className="kycReviewClientTypeModalTitle">
                  UBOs Identification
                </div>
                <div className="kycReviewClientTypeModalHint">
                  {Array.isArray(uboApiResult?.forms) ? "CDB 2020 forms required for the current ownership structure, pre-filled with the persons to declare." : "UBOs meeting the identification threshold based on the current ownership structure."}
                </div>
              </div>
              <button
                type="button"
                className="kycReviewClientTypeModalClose"
                onClick={() => setUboModalOpen(false)}
                aria-label="Close"
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>

            {uboApiLoading ? (
              <div className="kycReviewUboLoading">
                <span className="spinner-border spinner-border-sm" />
                <span>Identifying UBOs...</span>
              </div>
            ) : uboApiError ? (
              <div className="alert alert-warning mb-0">{uboApiError}</div>
            ) : (
              <div className="kycReviewUboModalBody">
                <UboIdentificationNotice result={uboApiResult} nodes={uboApiPayload.nodes} />
                {Array.isArray(uboApiResult?.forms) ? (
                  <KycCdbFormsPanel result={uboApiResult} />
                ) : (
                <>
                <div className="kycReviewUboSummary">
                  {uboApiResult?.meta?.reviewRequired
                        ? beneficialOwnerCount
                          ? `${beneficialOwnerCount} beneficial owner${beneficialOwnerCount === 1 ? "" : "s"} identified so far — review incomplete`
                          : "Beneficial owners not yet confirmed"
                        : `${beneficialOwnerCount} beneficial owner${beneficialOwnerCount === 1 ? "" : "s"} meeting threshold`}
                </div>

                {uboDirectShareholderRows.length ? (
                  <div className="kycReviewUboList">
                    <div className="kycReviewUboLegalTitle">Direct shareholders</div>
                    {uboDirectShareholderRows.map((row) => (
                      <div key={row.id} className="kycReviewUboRow">
                        <div className="kycReviewUboAvatar">
                          <img src={iconIndividual} alt="" aria-hidden="true" />
                        </div>
                        <div className="kycReviewUboRowMain">
                          <div className="kycReviewUboName">{row.name}</div>
                          <div className="kycReviewUboMeta">{row.type}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {uboApiRows.length ? (
                  <div className="kycReviewUboList">
                    <div className="kycReviewUboLegalTitle">Indirect UBOs</div>
                    {uboApiRows.map((row) => (
                      <div key={row.id} className="kycReviewUboRow">
                        <div className="kycReviewUboAvatar">
                          <img src={iconIndividual} alt="" aria-hidden="true" />
                        </div>
                        <div className="kycReviewUboRowMain">
                          <div className="kycReviewUboName">{row.name}</div>
                          <div className="kycReviewUboMeta">{row.type}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {!beneficialOwnerCount ? (
                  <div className="kycReviewUboEmpty">
                    {uboApiResult?.meta?.reviewRequired
                          ? "The assessment is incomplete. This does not mean there are no beneficial owners. Review the items above to complete identification."
                          : "No beneficial owner meeting threshold was returned for this ownership structure."}
                  </div>
                ) : null}

                {uboLegalArrangementRows.length ? (
                  <div className="kycReviewUboLegalBlock">
                    <div className="kycReviewUboLegalTitle">Legal arrangements</div>
                    <div className="kycReviewUboLegalArrangementRows">
                      {uboLegalArrangementRows.map((row) => (
                        <div
                          key={row.id}
                          className="kycReviewUboLegalArrangementCard"
                        >
                          <div className="kycReviewUboLegalArrangementLeft">
                            <div className="kycReviewUboLegalArrangementIcon">
                              <img
                                src={row.type === "Foundation" ? iconFoundation : iconTrust}
                                alt=""
                                aria-hidden="true"
                              />
                            </div>
                            <div className="kycReviewUboLegalArrangementIdentity">
                              <div className="kycReviewUboName">{row.name}</div>
                              <div className="kycReviewUboMeta">{row.type}</div>
                            </div>
                          </div>
                          <div className="kycReviewUboLegalParties">
                            {row.parties.length ? (
                              row.parties.map((party) => (
                                <div
                                  key={party.id}
                                  className="kycReviewUboLegalPartyRow"
                                >
                                  <div className="kycReviewUboAvatar">
                                    <img src={party.icon} alt="" aria-hidden="true" />
                                  </div>
                                  <div className="kycReviewUboRowMain">
                                    <div className="kycReviewUboName">
                                      {party.name}
                                    </div>
                                    <div className="kycReviewUboMeta">
                                      {party.meta}
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="kycReviewUboLegalEmptyParties">
                                No related individuals recorded.
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                </>
              )}
              </div>
            )}
          </motion.div>
        </div>
    </div>
  );
}
